import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  AlignJustify as ParagraphIcon,
  CheckSquare,
  CircleDot,
  Download,
  ImagePlus,
  ListOrdered,
  Save,
  Trash2,
  Type,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { EditorContent, useEditor } from '@tiptap/react';
import { Extension, Node as TiptapNode, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Mathematics, migrateMathStrings } from '@tiptap/extension-mathematics';
import { TableKit } from '@tiptap/extension-table';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view';
import katex from 'katex';
import { marked } from 'marked';
import 'katex/dist/katex.min.css';
import { useAuthStore } from '../../../stores/authStore';
import { useDocumentStore } from '../../../stores/documentStore';
import { functions, storage } from '../../../services/firebase';
import { InlineMathModal } from '../../math/InlineMathField';
import ResponseAreaConfigModal from '../ResponseAreaConfigModal';
import ImageUploadModal from '../ImageUploadModal';
import DocumentNarrationModal from '../DocumentNarrationModal';

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const PAGE_PADDING_MM = 25.4;
const PAGE_PADDING_TOP_MM = 12.7;
const PAGE_PADDING_BOTTOM_MM = 12.7;
const REGION_MIN_SPACE_PX = 20;
const BODY_WIDTH_STYLE = `calc(${PAGE_WIDTH_MM}mm - ${PAGE_PADDING_MM * 2}mm)`;
const getBodyHeightStyle = (topSpacePx, bottomSpacePx) =>
  `calc(${PAGE_HEIGHT_MM}mm - ${PAGE_PADDING_TOP_MM + PAGE_PADDING_BOTTOM_MM}mm - ${topSpacePx + bottomSpacePx}px)`;
const PAGE_GAP_PX = 32;
const PAGE_BODY_EDGE_INSET_PX = 12;
const KATEX_OPTIONS = {
  throwOnError: false,
  strict: false,
  macros: {
    // Unit expressions commonly place \cdotp inside \text{...}. KaTeX's
    // native \cdotp is math-only, so select a safe expansion for each mode.
    '\\cdotp': '\\TextOrMath{\\(\\cdot\\)}{\\mathpunct{\\cdot}}',
  },
};
const INLINE_INDENT_TEXT = String.fromCharCode(8195, 8195);

const editablePaginationKey = new PluginKey('editablePagination');
const SLASH_COMMAND_MENU_WIDTH_PX = 256;
const SLASH_COMMAND_MENU_HEIGHT_PX = 420;
const SLASH_COMMANDS = [
  { id: 'step', label: 'Step', icon: ListOrdered },
  { id: 'multiple-choice', label: 'Multiple Choice', icon: CircleDot },
  { id: 'multi-select', label: 'Multi Select', icon: CheckSquare },
  { id: 'short-answer', label: 'Short Answer', icon: Type },
  { id: 'paragraph', label: 'Paragraph', icon: ParagraphIcon },
  { id: 'image-upload', label: 'Image Upload', icon: ImagePlus },
];

const RESPONSE_AREA_TYPE_LABELS = {
  'multiple-choice': 'Multiple Choice Response',
  'multi-select': 'Multi Select Response',
  'short-answer': 'Short Answer Response',
  paragraph: 'Paragraph Response',
  'image-upload': 'Image Upload Response',
};

const escapeEditorHtml = (value = '') =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const parseResponseConfig = (value, fallback = {}) => {
  if (value && typeof value === 'object') return { ...fallback, ...value };
  try {
    return { ...fallback, ...JSON.parse(value || '{}') };
  } catch (_) {
    return fallback;
  }
};

const buildV2ResponseAreaPreviewHtml = (type, config = {}) => {
  const label = String(config.label || '').trim();
  const points = Number(config.points || 0);
  const options = Array.isArray(config.options)
    ? config.options.filter((option) => String(option || '').trim())
    : [];
  const meta = [
    points > 0 ? `${points} pt${points === 1 ? '' : 's'}` : '',
    config.required === false ? 'Optional' : 'Required',
  ].filter(Boolean).join(' • ');

  let body = '<div class="response-area-preview-line"></div>';
  if (type === 'multiple-choice' || type === 'multi-select') {
    body = options.length
      ? `<div class="response-area-preview-options">${options.map((option, index) => `
          <div class="response-area-preview-option">
            <span class="response-area-preview-marker">${type === 'multiple-choice' ? String.fromCharCode(65 + index) : index + 1}</span>
            <span class="response-area-preview-text">${escapeEditorHtml(option)}</span>
          </div>`).join('')}</div>`
      : '<div class="response-area-preview-empty">Add answer options in the response settings.</div>';
  } else if (type === 'paragraph') {
    body = '<div class="response-area-preview-lines"><div class="response-area-preview-line"></div><div class="response-area-preview-line"></div><div class="response-area-preview-line short"></div></div>';
  } else if (type === 'image-upload') {
    body = '<div class="response-area-preview-upload"><span class="response-area-preview-upload-icon">+</span><span>Image upload response</span></div>';
  }

  return `
    <div class="response-area-placeholder">
      <div class="response-area-preview-header">
        <div class="response-area-preview-title">✍ ${RESPONSE_AREA_TYPE_LABELS[type] || 'Response Area'}</div>
        <div class="response-area-preview-header-actions">
          ${meta ? `<div class="response-area-preview-meta">${escapeEditorHtml(meta)}</div>` : ''}
          <button type="button" class="response-area-preview-delete" data-response-action="delete" contenteditable="false" title="Delete response block" aria-label="Delete response block">×</button>
        </div>
      </div>
      ${label ? `<div class="response-area-preview-label">${escapeEditorHtml(label)}</div>` : ''}
      ${body}
    </div>
  `;
};

const sanitizeStepEmbedHtml = (value = '') => {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = String(value || '').trim();
  wrapper.querySelectorAll('script').forEach((node) => node.remove());
  return wrapper.innerHTML.trim();
};

const getSafeStepEmbedUrl = (value = '') => {
  try {
    const url = new URL(String(value || '').trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : '';
  } catch (_) {
    return '';
  }
};

const getStepYouTubeEmbedUrl = (value = '') => {
  const raw = String(value || '').trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(raw)) return `https://www.youtube-nocookie.com/embed/${raw}`;
  try {
    const url = new URL(raw);
    const host = url.hostname.replace(/^www\./, '').toLowerCase();
    let id = '';
    if (host === 'youtu.be') id = url.pathname.slice(1).split('/')[0];
    else if (['youtube.com', 'm.youtube.com', 'music.youtube.com'].includes(host)) {
      if (url.pathname === '/watch') id = url.searchParams.get('v') || '';
      else if (url.pathname.startsWith('/shorts/') || url.pathname.startsWith('/embed/')) {
        id = url.pathname.split('/')[2] || '';
      }
    }
    return /^[a-zA-Z0-9_-]{11}$/.test(id)
      ? `https://www.youtube-nocookie.com/embed/${id}`
      : '';
  } catch (_) {
    return '';
  }
};

const buildV2StepAudioHtml = (audio = {}) => `
  <div class="document-step-audio-header">
    <div>
      <div class="document-step-audio-title">Step audio</div>
      <div class="document-step-audio-meta">${escapeEditorHtml(audio.name || 'Step audio')}${Number(audio.duration) > 0 ? ` • ${Math.floor(Number(audio.duration))}s` : ''}</div>
    </div>
    <div class="document-step-audio-actions">
      <button type="button" class="document-step-audio-action" data-step-audio-action="replace">Replace</button>
      <button type="button" class="document-step-audio-action" data-step-audio-action="remove">Remove</button>
    </div>
  </div>
  <audio controls preload="metadata" src="${escapeEditorHtml(audio.url || '')}">
    <source src="${escapeEditorHtml(audio.url || '')}" type="${escapeEditorHtml(audio.contentType || 'audio/webm')}" />
  </audio>
`;

const buildV2StepEmbedHtml = (attrs = {}) => {
  if (attrs.kind === 'html') {
    return `
      <div class="document-step-embed-label"><span class="document-step-embed-icon">&lt;&gt;</span>HTML Embed</div>
      <div class="document-step-embed-content">${sanitizeStepEmbedHtml(attrs.html || '')}</div>
      <button type="button" class="document-step-embed-edit" data-step-embed-action="edit">Edit embed</button>
    `;
  }
  const src = attrs.kind === 'youtube'
    ? getStepYouTubeEmbedUrl(attrs.src)
    : getSafeStepEmbedUrl(attrs.src);
  return `
    ${attrs.kind === 'url' ? '<div class="document-step-embed-label"><span class="document-step-embed-icon">↗</span>Embed URL</div>' : ''}
    <div class="document-step-embed-content">
      <iframe src="${escapeEditorHtml(src)}" title="${attrs.kind === 'youtube' ? 'YouTube video' : 'Embedded content'}" loading="lazy"${attrs.kind === 'youtube' ? ' allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen' : ' referrerpolicy="no-referrer-when-downgrade"'}></iframe>
    </div>
    <button type="button" class="document-step-embed-edit" data-step-embed-action="edit">Edit embed</button>
  `;
};

const clampImageWidth = (width) => Math.max(60, Math.min(600, Math.round(Number(width) || 300)));
const removeInlineIndent = (state, dispatch) => {
  const { from } = state.selection;
  const indentLength = INLINE_INDENT_TEXT.length;
  const beforeFrom = Math.max(0, from - indentLength);
  const beforeText = state.doc.textBetween(beforeFrom, from, '', '');
  const afterText = state.doc.textBetween(from, from + indentLength, '', '');
  const deleteFrom = beforeText === INLINE_INDENT_TEXT
    ? beforeFrom
    : afterText === INLINE_INDENT_TEXT
      ? from
      : null;
  if (deleteFrom === null) return false;
  if (dispatch) {
    const transaction = state.tr.delete(deleteFrom, deleteFrom + indentLength);
    transaction.setSelection(TextSelection.create(transaction.doc, deleteFrom));
    dispatch(transaction.scrollIntoView());
  }
  return true;
};

const adjustSelectedBlockIndent = (state, dispatch, delta) => {
  const positions = new Set();
  const { $from, from, to } = state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).isTextblock) {
      positions.add($from.before(depth));
      break;
    }
  }
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.isTextblock) positions.add(pos);
    return !node.isTextblock;
  });
  if (!positions.size) return false;
  if (dispatch) {
    const transaction = state.tr;
    positions.forEach((position) => {
      const node = transaction.doc.nodeAt(position);
      if (!node?.isTextblock || !['paragraph', 'heading'].includes(node.type.name)) return;
      const current = Math.max(0, Number(node.attrs.blockIndent) || 0);
      const blockIndent = Math.max(0, Math.min(8, current + delta));
      transaction.setNodeMarkup(position, undefined, { ...node.attrs, blockIndent });
    });
    dispatch(transaction.scrollIntoView());
  }
  return true;
};

const SectionDivider = TiptapNode.create({
  name: 'sectionDivider',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      dividerId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-section-divider-id'),
        renderHTML: (attributes) => attributes.dividerId
          ? { 'data-section-divider-id': attributes.dividerId }
          : {},
      },
      label: {
        default: 'Section label',
        parseHTML: (element) => element.querySelector('.section-divider-label')?.textContent || 'Section label',
        renderHTML: () => ({}),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div.section-divider-block' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'section-divider-block', contenteditable: 'false' }),
      ['span', { class: 'section-divider-line', 'aria-hidden': 'true' }],
      ['span', { class: 'section-divider-label', contenteditable: 'true', spellcheck: 'true' }, node.attrs.label],
    ];
  },
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = document.createElement('div');
      const line = document.createElement('span');
      const label = document.createElement('span');
      dom.className = 'section-divider-block';
      dom.contentEditable = 'false';
      line.className = 'section-divider-line';
      line.setAttribute('aria-hidden', 'true');
      label.className = 'section-divider-label';
      label.contentEditable = 'true';
      label.spellcheck = true;

      const updateDom = (currentNode) => {
        if (currentNode.attrs.dividerId) dom.dataset.sectionDividerId = currentNode.attrs.dividerId;
        else delete dom.dataset.sectionDividerId;
        if (label.textContent !== currentNode.attrs.label) label.textContent = currentNode.attrs.label || '';
      };
      const saveLabel = () => {
        const pos = getPos();
        if (!Number.isFinite(pos)) return;
        const current = editor.state.doc.nodeAt(pos);
        if (!current || current.type.name !== 'sectionDivider') return;
        editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, {
          ...current.attrs,
          label: label.textContent || '',
        }));
      };
      label.addEventListener('input', saveLabel);
      label.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        label.blur();
        const pos = getPos();
        if (Number.isFinite(pos)) {
          editor.chain().focus().setTextSelection(pos + node.nodeSize).run();
        }
      });
      dom.append(line, label);
      updateDom(node);

      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'sectionDivider') return false;
          node = updatedNode;
          updateDom(updatedNode);
          return true;
        },
        stopEvent: (event) => label.contains(event.target),
        ignoreMutation: (mutation) => label.contains(mutation.target),
      };
    };
  },
});

const ResponseAreaBlock = TiptapNode.create({
  name: 'responseArea',
  group: 'block',
  atom: true,
  selectable: true,

  addOptions() {
    return {
      onOpen: () => {},
      onDelete: () => {},
    };
  },

  addAttributes() {
    return {
      responseId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-response-id'),
        renderHTML: (attributes) => attributes.responseId
          ? { 'data-response-id': attributes.responseId }
          : {},
      },
      responseType: {
        default: 'short-answer',
        parseHTML: (element) => element.getAttribute('data-response-type') || 'short-answer',
        renderHTML: (attributes) => ({ 'data-response-type': attributes.responseType || 'short-answer' }),
      },
      configJson: {
        default: '{}',
        parseHTML: (element) => {
          const responseId = element.getAttribute('data-response-id');
          const responseType = element.getAttribute('data-response-type') || 'short-answer';
          const storedConfig = responseId
            ? useDocumentStore.getState().currentDocument?.assessment?.responseAreas?.[responseId]
            : null;
          const embeddedConfig = element.getAttribute('data-response-config');
          return JSON.stringify(parseResponseConfig(embeddedConfig, storedConfig || {
            type: responseType,
            label: element.getAttribute('data-response-label') || '',
            required: element.getAttribute('data-response-required') !== 'false',
            points: Number(element.getAttribute('data-response-points') || 1),
          }));
        },
        renderHTML: (attributes) => ({ 'data-response-config': attributes.configJson || '{}' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div.response-area-block' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const config = parseResponseConfig(node.attrs.configJson, {
      type: node.attrs.responseType,
      required: true,
      points: 1,
    });
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        class: 'response-area-block',
        contenteditable: 'false',
        'data-response-label': config.label || '',
        'data-response-points': String(Number(config.points || 0)),
        'data-response-required': config.required === false ? 'false' : 'true',
      }),
      ['div', { class: 'response-area-placeholder' },
        ['div', { class: 'response-area-preview-header' },
          ['div', { class: 'response-area-preview-title' }, `✍ ${RESPONSE_AREA_TYPE_LABELS[node.attrs.responseType] || 'Response Area'}`],
          ['div', { class: 'response-area-preview-meta' }, `${Number(config.points || 0)} pts`],
        ],
        ...(config.label ? [['div', { class: 'response-area-preview-label' }, config.label]] : []),
      ],
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const dom = document.createElement('div');
      dom.className = 'response-area-block';
      dom.contentEditable = 'false';

      const syncDom = (currentNode) => {
        const config = parseResponseConfig(currentNode.attrs.configJson, {
          type: currentNode.attrs.responseType,
          required: true,
          points: 1,
        });
        if (currentNode.attrs.responseId) dom.dataset.responseId = currentNode.attrs.responseId;
        dom.dataset.responseType = currentNode.attrs.responseType || config.type || 'short-answer';
        dom.dataset.responseLabel = config.label || '';
        dom.dataset.responsePoints = String(Number(config.points || 0));
        dom.dataset.responseRequired = config.required === false ? 'false' : 'true';
        dom.dataset.responseConfig = JSON.stringify(config);
        dom.innerHTML = buildV2ResponseAreaPreviewHtml(dom.dataset.responseType, config);
      };

      dom.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.target.closest('[data-response-action="delete"]')) {
          this.options.onDelete(node.attrs.responseId, getPos(), node.nodeSize);
          return;
        }
        this.options.onOpen(node.attrs.responseId);
      });
      syncDom(node);

      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) return false;
          node = updatedNode;
          syncDom(updatedNode);
          return true;
        },
        selectNode: () => dom.classList.add('ProseMirror-selectednode'),
        deselectNode: () => dom.classList.remove('ProseMirror-selectednode'),
        stopEvent: () => true,
        ignoreMutation: () => true,
      };
    };
  },
});

const DocumentStepAudio = TiptapNode.create({
  name: 'documentStepAudio',
  group: 'block',
  atom: true,
  selectable: true,

  addOptions() {
    return { onReplace: () => {}, onRemove: () => {} };
  },

  addAttributes() {
    return {
      url: { default: '', parseHTML: (element) => element.querySelector('audio')?.getAttribute('src') || '' },
      name: { default: 'Step audio', parseHTML: (element) => element.querySelector('.document-step-audio-meta')?.textContent || 'Step audio' },
      contentType: { default: 'audio/webm', parseHTML: (element) => element.querySelector('source')?.getAttribute('type') || 'audio/webm' },
      path: { default: '', parseHTML: (element) => element.getAttribute('data-step-audio-path') || '' },
      duration: { default: 0, parseHTML: () => 0 },
    };
  },

  parseHTML() {
    return [{ tag: 'div.document-step-audio-block' }];
  },

  renderHTML({ node }) {
    return [
      'div',
      {
        class: 'document-step-audio-block',
        contenteditable: 'false',
        'data-step-audio-path': node.attrs.path || '',
        'data-step-audio-url': node.attrs.url || '',
        'data-step-audio-name': node.attrs.name || 'Step audio',
        'data-step-audio-type': node.attrs.contentType || 'audio/webm',
        'data-step-audio-duration': String(Number(node.attrs.duration) || 0),
      },
      ['div', { class: 'document-step-audio-title' }, 'Step audio'],
      ['audio', { controls: 'controls', preload: 'metadata', src: node.attrs.url || '' }],
    ];
  },

  addNodeView() {
    return ({ node, getPos }) => {
      const dom = document.createElement('div');
      dom.className = 'document-step-audio-block';
      dom.contentEditable = 'false';
      const sync = (currentNode) => {
        dom.dataset.stepAudioPath = currentNode.attrs.path || '';
        dom.dataset.stepAudioUrl = currentNode.attrs.url || '';
        dom.dataset.stepAudioName = currentNode.attrs.name || 'Step audio';
        dom.dataset.stepAudioType = currentNode.attrs.contentType || 'audio/webm';
        dom.dataset.stepAudioDuration = String(Number(currentNode.attrs.duration) || 0);
        dom.innerHTML = buildV2StepAudioHtml(currentNode.attrs);
      };
      dom.addEventListener('click', (event) => {
        const action = event.target.closest('[data-step-audio-action]')?.dataset.stepAudioAction;
        if (!action) return;
        event.preventDefault();
        event.stopPropagation();
        if (action === 'replace') this.options.onReplace(getPos(), node.attrs);
        if (action === 'remove') this.options.onRemove(getPos(), node.nodeSize, node.attrs.path);
      });
      sync(node);
      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) return false;
          node = updatedNode;
          sync(updatedNode);
          return true;
        },
        stopEvent: () => true,
        ignoreMutation: () => true,
      };
    };
  },
});

const DocumentStepEmbed = TiptapNode.create({
  name: 'documentStepEmbed',
  group: 'block',
  atom: true,
  selectable: true,

  addOptions() {
    return { onEdit: () => {} };
  },

  addAttributes() {
    return {
      kind: { default: 'html', parseHTML: (element) => element.getAttribute('data-step-embed-kind') || 'html' },
      src: { default: '', parseHTML: (element) => element.getAttribute('data-embed-src') || '' },
      html: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-embed-html') || element.querySelector('.document-step-embed-content')?.innerHTML || '',
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div.document-step-embed-block' }];
  },

  renderHTML({ node }) {
    return [
      'div',
      {
        class: 'document-step-embed-block',
        contenteditable: 'false',
        'data-step-embed-kind': node.attrs.kind,
        'data-embed-src': node.attrs.src || '',
        'data-embed-html': node.attrs.html || '',
      },
      ['div', { class: 'document-step-embed-content', 'data-v2-step-embed-placeholder': 'true' }],
    ];
  },

  addNodeView() {
    return ({ node, getPos }) => {
      const dom = document.createElement('div');
      dom.className = 'document-step-embed-block';
      dom.contentEditable = 'false';
      const sync = (currentNode) => {
        dom.dataset.stepEmbedKind = currentNode.attrs.kind;
        dom.dataset.embedSrc = currentNode.attrs.src || '';
        dom.dataset.embedHtml = currentNode.attrs.html || '';
        dom.innerHTML = buildV2StepEmbedHtml(currentNode.attrs);
      };
      dom.addEventListener('click', (event) => {
        if (!event.target.closest('[data-step-embed-action="edit"]')) return;
        event.preventDefault();
        event.stopPropagation();
        this.options.onEdit(getPos(), node.attrs);
      });
      sync(node);
      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) return false;
          node = updatedNode;
          sync(updatedNode);
          return true;
        },
        stopEvent: () => true,
        ignoreMutation: () => true,
      };
    };
  },
});

const DocumentStepBlock = TiptapNode.create({
  name: 'documentStep',
  group: 'block',
  content: 'block+',
  defining: true,
  isolating: true,

  addOptions() {
    return {
      onOpenMath: () => {},
      onOpenImage: () => {},
      onOpenAudio: () => {},
      onOpenEmbed: () => {},
    };
  },

  addAttributes() {
    return {
      stepId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-step-id'),
        renderHTML: (attributes) => attributes.stepId ? { 'data-step-id': attributes.stepId } : {},
      },
      stepNumber: {
        default: '',
        parseHTML: (element) => element.querySelector('.document-step-number')?.textContent || '',
        renderHTML: () => ({}),
      },
      title: {
        default: '',
        parseHTML: (element) => element.querySelector('.document-step-title')?.textContent || '',
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{
      tag: 'div.document-step-block',
      contentElement: '.document-step-body',
    }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'document-step-block', contenteditable: 'false' }),
      ['div', { class: 'document-step-shell' },
        ['button', {
          type: 'button',
          class: 'document-step-delete-button',
          'data-step-action': 'delete',
          contenteditable: 'false',
          title: 'Delete step',
          'aria-label': 'Delete step',
        },
          ['svg', { viewBox: '0 0 24 24', 'aria-hidden': 'true', focusable: 'false' },
            ['path', { d: 'M3 6h18' }],
            ['path', { d: 'M8 6V4h8v2' }],
            ['path', { d: 'M19 6l-1 14H6L5 6' }],
            ['path', { d: 'M10 11v6' }],
            ['path', { d: 'M14 11v6' }],
          ],
        ],
        ['div', { class: 'document-step-header' },
          ['span', { class: 'document-step-chip' }, 'Step'],
          ['span', { class: 'document-step-number', contenteditable: 'true', 'data-placeholder': 'No.' }, node.attrs.stepNumber || ''],
          ['span', { class: 'document-step-title', contenteditable: 'true', 'data-placeholder': 'Optional step name' }, node.attrs.title || ''],
        ],
        ['div', { class: 'document-step-toolbar', contenteditable: 'false' },
          ['button', { type: 'button', class: 'document-step-action', 'data-step-action': 'focus' }, 'Text'],
          ['button', { type: 'button', class: 'document-step-action', 'data-step-action': 'math' }, 'Math'],
          ['button', { type: 'button', class: 'document-step-action', 'data-step-action': 'image' }, 'Image'],
          ['button', { type: 'button', class: 'document-step-action', 'data-step-action': 'audio' }, 'Audio'],
          ['button', { type: 'button', class: 'document-step-action', 'data-step-action': 'html' }, 'HTML'],
          ['button', { type: 'button', class: 'document-step-action', 'data-step-action': 'url' }, 'URL'],
          ['button', { type: 'button', class: 'document-step-action', 'data-step-action': 'youtube' }, 'YouTube'],
          ['span', { class: 'document-step-toolbar-hint' }, 'Paste with Ctrl+V'],
        ],
        ['div', { class: 'document-step-body' }, 0],
      ],
    ];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = document.createElement('div');
      const shell = document.createElement('div');
      const deleteButton = document.createElement('button');
      const header = document.createElement('div');
      const chip = document.createElement('span');
      const number = document.createElement('span');
      const title = document.createElement('span');
      const toolbar = document.createElement('div');
      const contentDOM = document.createElement('div');

      dom.className = 'document-step-block';
      dom.contentEditable = 'false';
      shell.className = 'document-step-shell';
      deleteButton.type = 'button';
      deleteButton.className = 'document-step-delete-button';
      deleteButton.contentEditable = 'false';
      deleteButton.dataset.stepAction = 'delete';
      deleteButton.title = 'Delete step';
      deleteButton.setAttribute('aria-label', 'Delete step');
      deleteButton.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M3 6h18"></path>
          <path d="M8 6V4h8v2"></path>
          <path d="M19 6l-1 14H6L5 6"></path>
          <path d="M10 11v6"></path>
          <path d="M14 11v6"></path>
        </svg>
      `;
      header.className = 'document-step-header';
      chip.className = 'document-step-chip';
      chip.textContent = 'Step';
      number.className = 'document-step-number';
      number.contentEditable = 'true';
      number.dataset.placeholder = 'No.';
      title.className = 'document-step-title';
      title.contentEditable = 'true';
      title.dataset.placeholder = 'Optional step name';
      toolbar.className = 'document-step-toolbar';
      toolbar.contentEditable = 'false';
      toolbar.innerHTML = `
        <button type="button" class="document-step-action" data-step-action="focus">Text</button>
        <button type="button" class="document-step-action" data-step-action="math">Math</button>
        <button type="button" class="document-step-action" data-step-action="image">Image</button>
        <button type="button" class="document-step-action" data-step-action="audio">Audio</button>
        <button type="button" class="document-step-action" data-step-action="html">HTML</button>
        <button type="button" class="document-step-action" data-step-action="url">URL</button>
        <button type="button" class="document-step-action" data-step-action="youtube">YouTube</button>
        <span class="document-step-toolbar-hint">Paste with Ctrl+V</span>
      `;
      contentDOM.className = 'document-step-body';
      contentDOM.contentEditable = 'true';

      const updateAttrs = () => {
        const pos = getPos();
        if (!Number.isFinite(pos)) return;
        editor.commands.command(({ tr }) => {
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            stepNumber: number.textContent || '',
            title: title.textContent || '',
          });
          return true;
        });
      };
      const syncDom = (currentNode) => {
        if (currentNode.attrs.stepId) dom.dataset.stepId = currentNode.attrs.stepId;
        if (number.textContent !== currentNode.attrs.stepNumber) number.textContent = currentNode.attrs.stepNumber || '';
        if (title.textContent !== currentNode.attrs.title) title.textContent = currentNode.attrs.title || '';
        const hasEmbed = currentNode.content.content.some((child) => child.type.name === 'documentStepEmbed');
        toolbar.querySelectorAll(
          '[data-step-action="html"], [data-step-action="url"], [data-step-action="youtube"]'
        ).forEach((button) => {
          button.style.display = hasEmbed ? 'none' : '';
        });
      };

      number.addEventListener('input', updateAttrs);
      title.addEventListener('input', updateAttrs);
      deleteButton.addEventListener('click', (event) => {
        event.preventDefault();
        const pos = getPos();
        if (Number.isFinite(pos)) editor.commands.deleteRange({ from: pos, to: pos + node.nodeSize });
      });
      toolbar.addEventListener('click', (event) => {
        const action = event.target.closest('[data-step-action]')?.dataset.stepAction;
        if (!action) return;
        event.preventDefault();
        const pos = getPos();
        if (!Number.isFinite(pos)) return;
        const currentSelection = editor.state.selection;
        const stepEnd = pos + node.nodeSize - 1;
        const target = currentSelection.from > pos && currentSelection.from < stepEnd
          ? currentSelection.from
          : stepEnd;
        editor.chain().focus().setTextSelection(target).run();
        if (action === 'math') this.options.onOpenMath(target);
        if (action === 'image') this.options.onOpenImage(target);
        if (action === 'audio') this.options.onOpenAudio(pos, target);
        if (action === 'html' || action === 'url' || action === 'youtube') {
          this.options.onOpenEmbed(action, pos, target);
        }
      });

      header.append(chip, number, title);
      shell.append(deleteButton, header, toolbar, contentDOM);
      dom.append(shell);
      syncDom(node);

      return {
        dom,
        contentDOM,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) return false;
          node = updatedNode;
          syncDom(updatedNode);
          return true;
        },
        stopEvent: (event) => !contentDOM.contains(event.target),
        ignoreMutation: (mutation) => !contentDOM.contains(mutation.target),
      };
    };
  },
});

const ResizableImage = TiptapNode.create({
  name: 'resizableImage',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      src: {
        default: '',
        parseHTML: (element) => element.querySelector('img')?.getAttribute('src') || element.getAttribute('src') || '',
      },
      alt: {
        default: '',
        parseHTML: (element) => element.querySelector('img')?.getAttribute('alt') || '',
      },
      width: {
        default: 300,
        parseHTML: (element) => clampImageWidth(parseFloat(element.style.width) || element.getBoundingClientRect().width || 300),
      },
      align: {
        default: 'left',
        parseHTML: (element) => element.getAttribute('data-image-align') || 'left',
      },
      naturalWidth: {
        default: null,
        parseHTML: (element) => Number(element.querySelector('img')?.getAttribute('width')) || null,
      },
      naturalHeight: {
        default: null,
        parseHTML: (element) => Number(element.querySelector('img')?.getAttribute('height')) || null,
      },
    };
  },
  parseHTML() {
    return [
      { tag: 'div.image-container' },
      { tag: 'span.image-container' },
      { tag: 'img[src]' },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const { src, alt, width, align, naturalWidth, naturalHeight } = HTMLAttributes;
    return [
      'div',
      mergeAttributes({
        class: 'image-container',
        'data-image-align': align || 'left',
        style: `width:${clampImageWidth(width)}px;max-width:100%;position:relative;`,
      }),
      ['img', {
        src,
        alt: alt || '',
        width: naturalWidth || undefined,
        height: naturalHeight || undefined,
        draggable: 'false',
        style: 'width:100%;height:auto;display:block;',
      }],
    ];
  },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const dom = document.createElement('div');
      const image = document.createElement('img');
      const controls = document.createElement('div');
      dom.className = 'image-container v2-image-node';
      dom.setAttribute('contenteditable', 'false');
      dom.draggable = true;
      image.draggable = false;
      controls.className = 'v2-image-controls';

      const updateDom = (currentNode) => {
        const attrs = currentNode.attrs;
        dom.dataset.imageAlign = attrs.align || 'left';
        dom.style.width = `${clampImageWidth(attrs.width)}px`;
        image.src = attrs.src || '';
        image.alt = attrs.alt || '';
        if (attrs.naturalWidth) image.setAttribute('width', String(attrs.naturalWidth));
        if (attrs.naturalHeight) image.setAttribute('height', String(attrs.naturalHeight));
      };

      const setAttributes = (changes) => {
        const pos = getPos();
        if (!Number.isFinite(pos)) return;
        const current = editor.state.doc.nodeAt(pos);
        if (!current || current.type.name !== 'resizableImage') return;
        editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...current.attrs, ...changes }));
      };

      ['left', 'center', 'right'].forEach((alignment) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'v2-image-control-button';
        button.textContent = alignment === 'left' ? 'L' : alignment === 'center' ? 'C' : 'R';
        button.title = `Align ${alignment}`;
        button.addEventListener('mousedown', (event) => event.preventDefault());
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          setAttributes({ align: alignment });
        });
        controls.appendChild(button);
      });
      const deleteButton = document.createElement('button');
      deleteButton.type = 'button';
      deleteButton.className = 'v2-image-control-button v2-image-delete-button';
      deleteButton.textContent = '×';
      deleteButton.title = 'Delete image';
      deleteButton.addEventListener('mousedown', (event) => event.preventDefault());
      deleteButton.addEventListener('click', (event) => {
        event.stopPropagation();
        const pos = getPos();
        if (Number.isFinite(pos)) editor.view.dispatch(editor.state.tr.delete(pos, pos + node.nodeSize));
      });
      controls.appendChild(deleteButton);

      ['top-left', 'top-right', 'bottom-left', 'bottom-right'].forEach((corner) => {
        const handle = document.createElement('span');
        handle.className = `v2-image-resize-handle v2-image-resize-${corner}`;
        handle.dataset.resizeCorner = corner;
        handle.addEventListener('mousedown', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const startX = event.clientX;
          const startWidth = dom.getBoundingClientRect().width;
          const onMove = (moveEvent) => {
            const delta = corner.includes('right') ? moveEvent.clientX - startX : startX - moveEvent.clientX;
            dom.style.width = `${clampImageWidth(startWidth + delta)}px`;
          };
          const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            setAttributes({ width: clampImageWidth(parseFloat(dom.style.width)) });
          };
          document.addEventListener('mousemove', onMove);
          document.addEventListener('mouseup', onUp);
        });
        dom.appendChild(handle);
      });

      dom.appendChild(image);
      dom.appendChild(controls);
      updateDom(node);
      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'resizableImage') return false;
          node = updatedNode;
          updateDom(updatedNode);
          return true;
        },
        selectNode: () => dom.classList.add('v2-image-selected'),
        deselectNode: () => dom.classList.remove('v2-image-selected'),
        stopEvent: (event) => !!event.target.closest?.('.v2-image-controls, .v2-image-resize-handle'),
      };
    };
  },
});

const TextIndent = Extension.create({
  name: 'textIndent',
  addGlobalAttributes() {
    return [{
      types: ['paragraph', 'heading'],
      attributes: {
        blockIndent: {
          default: 0,
          parseHTML: (element) => Number.parseInt(element.getAttribute('data-block-indent') || '0', 10) || 0,
          renderHTML: (attributes) => attributes.blockIndent > 0
            ? {
                'data-block-indent': attributes.blockIndent,
                style: `padding-left:${attributes.blockIndent * 2}em`,
              }
            : {},
        },
      },
    }];
  },
  addCommands() {
    return {
      indentContent: () => ({ editor, chain, state, dispatch }) => {
        if (editor.isActive('listItem')) {
          return chain().sinkListItem('listItem').run() || true;
        }
        return adjustSelectedBlockIndent(state, dispatch, 1);
      },
      outdentContent: () => ({ editor, chain, state, dispatch }) => {
        if (editor.isActive('listItem')) {
          return chain().liftListItem('listItem').run() || true;
        }
        return adjustSelectedBlockIndent(state, dispatch, -1) || removeInlineIndent(state, dispatch);
      },
    };
  },
  addKeyboardShortcuts() {
    return {
      Tab: () => this.editor.commands.indentContent(),
      'Shift-Tab': () => this.editor.commands.outdentContent(),
    };
  },
});

const ListMarkerStyle = Extension.create({
  name: 'listMarkerStyle',
  addGlobalAttributes() {
    return [{
      types: ['listItem'],
      attributes: {
        markerFontSize: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-list-marker-font-size'),
          renderHTML: (attributes) => attributes.markerFontSize
            ? {
                'data-list-marker-font-size': attributes.markerFontSize,
                style: `--list-marker-font-size:${attributes.markerFontSize}`,
              }
            : {},
        },
        markerFontWeight: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-list-marker-font-weight'),
          renderHTML: (attributes) => attributes.markerFontWeight
            ? {
                'data-list-marker-font-weight': attributes.markerFontWeight,
                style: `--list-marker-font-weight:${attributes.markerFontWeight}`,
              }
            : {},
        },
      },
    }];
  },
});

const findAncestorDomBlock = (view, pos, typeName) => {
  const resolved = view.state.doc.resolve(pos);
  for (let depth = resolved.depth; depth > 0; depth -= 1) {
    if (resolved.node(depth).type.name !== typeName) continue;
    const blockPos = resolved.before(depth);
    const dom = view.nodeDOM(blockPos);
    if (dom instanceof HTMLElement) {
      return { pos: blockPos, rect: dom.getBoundingClientRect() };
    }
  }
  return null;
};

const findNaturalBreak = (view, targetBottom) => {
  let firstBelow = null;
  let crossingTextblock = null;
  let crossingAtom = null;

  view.state.doc.descendants((node, pos) => {
    if (!node.isBlock) return true;
    const dom = view.nodeDOM(pos);
    if (!(dom instanceof HTMLElement)) return true;
    const rect = dom.getBoundingClientRect();
    if (node.type.name === 'documentStep') {
      if (rect.top >= targetBottom - 0.5) {
        if (!firstBelow || rect.top < firstBelow.rect.top) firstBelow = { pos, rect };
      } else if (rect.bottom > targetBottom + 0.5 && !crossingAtom) {
        crossingAtom = { pos, rect };
      }
      return false;
    }
    if (rect.top >= targetBottom - 0.5) {
      if (!firstBelow || rect.top < firstBelow.rect.top) firstBelow = { pos, rect };
      return false;
    }
    if (rect.bottom > targetBottom + 0.5) {
      if (node.isTextblock) crossingTextblock = { node, pos, rect, dom };
      else if (node.isAtom && !crossingAtom) crossingAtom = { pos, rect };
    }
    return true;
  });

  if (crossingTextblock) {
    const { node, pos, rect, dom } = crossingTextblock;
    const tableRow = findAncestorDomBlock(view, pos, 'tableRow');
    if (tableRow) return { pos: tableRow.pos, y: tableRow.rect.top };

    const computedLineHeight = parseFloat(window.getComputedStyle(dom).lineHeight);
    const lineHeight = Number.isFinite(computedLineHeight) ? computedLineHeight : 22.4;
    const spaceBeforeBreak = targetBottom - rect.top;

    // Keep short blocks together, but do not push a long paragraph or list item
    // wholesale to the next page. Two rendered lines on the current page is a
    // useful minimum that avoids both orphaned lines and large blank page tails.
    if (spaceBeforeBreak < lineHeight * 2) {
      return { pos, y: rect.top };
    }
    // Inline atoms and wrapped math make coordsAtPos non-monotonic enough that
    // binary search can select a line which still fits. Check each document
    // position in order and break only at the first genuinely overflowing line.
    for (let candidate = pos + 1; candidate < pos + node.nodeSize; candidate += 1) {
      const coords = view.coordsAtPos(candidate);
      if (coords.bottom > targetBottom + 0.5) {
        return { pos: candidate, y: coords.top || rect.top };
      }
    }
    return { pos, y: rect.top };
  }
  if (crossingAtom) return { pos: crossingAtom.pos, y: crossingAtom.rect.top };
  if (firstBelow) return { pos: firstBelow.pos, y: firstBelow.rect.top };
  return null;
};

const createPageBreakDecoration = (record, index) => Decoration.widget(
  record.pos,
  () => {
    const spacer = document.createElement('span');
    spacer.className = 'v2-page-break-spacer';
    spacer.dataset.v2PageBreakIndex = String(index);
    spacer.setAttribute('contenteditable', 'false');
    spacer.setAttribute('aria-hidden', 'true');
    spacer.style.height = `${record.height}px`;
    if (record.containerMask) {
      spacer.classList.add('v2-page-break-spacer--container-mask');
      spacer.style.setProperty('--v2-break-mask-left', `${record.containerMask.left}px`);
      spacer.style.setProperty('--v2-break-mask-width', `${record.containerMask.width}px`);
      spacer.style.setProperty('--v2-break-page-bottom', `${record.containerMask.pageBottom}px`);
      spacer.style.setProperty('--v2-break-next-page-top', `${record.containerMask.nextPageTop}px`);
    }
    return spacer;
  },
  { side: -1, key: `v2-page-break-${index}-${record.pos}-${record.height}` }
);

const EditablePagination = Extension.create({
  name: 'editablePagination',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: editablePaginationKey,
        state: {
          init: (_, state) => DecorationSet.empty,
          apply: (transaction, decorations) => {
            const replacement = transaction.getMeta(editablePaginationKey);
            if (replacement) return replacement;
            return decorations.map(transaction.mapping, transaction.doc);
          },
        },
        props: {
          decorations: (state) => editablePaginationKey.getState(state),
        },
        view: (view) => {
          const initialCanvas = view.dom.closest('.v2-editable-pages');
          if (initialCanvas?.dataset.v2PaginationMirror === 'true') {
            return {
              update: (nextView) => { view = nextView; },
              destroy: () => {},
            };
          }

          let frame = null;
          let delayTimer = null;
          let layoutVersion = 0;
          let dirtyFrom = 0;
          let canReuseLayout = false;
          let destroyed = false;
          const clearTimer = () => {
            if (frame !== null) cancelAnimationFrame(frame);
            if (delayTimer !== null) clearTimeout(delayTimer);
            frame = null;
            delayTimer = null;
          };
          const measure = async (version) => {
            frame = null;
            if (destroyed || version !== layoutVersion) return;
            const canvas = view.dom.closest('.v2-editable-pages');
            const guides = Array.from(canvas?.querySelectorAll('[data-v2-page-body-guide]') || []);
            if (!canvas || !guides.length) return;

            if (guides.length === 1) {
              if (editablePaginationKey.getState(view.state)?.find().length) {
                view.dispatch(view.state.tr.setMeta(editablePaginationKey, DecorationSet.empty));
              }
              const lastContentNode = Array.from(view.dom.children).at(-1);
              const contentBottom = lastContentNode?.getBoundingClientRect().bottom || view.dom.getBoundingClientRect().top;
              const guide = guides[0].getBoundingClientRect();
              if (contentBottom > guide.bottom - PAGE_BODY_EDGE_INSET_PX) {
                canvas.dispatchEvent(new CustomEvent('v2surfacepagecount', { detail: 2 }));
              }
              return;
            }

            const liveSpacers = Array.from(view.dom.querySelectorAll('.v2-page-break-spacer'));
            const existingBreakRecords = editablePaginationKey.getState(view.state)
              ?.find()
              .sort((a, b) => a.from - b.from)
              .map((decoration, index) => {
                const spacer = liveSpacers[index];
                const readMaskValue = (name) => parseFloat(spacer?.style.getPropertyValue(name)) || 0;
                const containerMask = spacer?.classList.contains('v2-page-break-spacer--container-mask')
                  ? {
                      left: readMaskValue('--v2-break-mask-left'),
                      width: readMaskValue('--v2-break-mask-width'),
                      pageBottom: readMaskValue('--v2-break-page-bottom'),
                      nextPageTop: readMaskValue('--v2-break-next-page-top'),
                    }
                  : null;
                return {
                  pos: decoration.from,
                  height: Math.max(1, Math.round(parseFloat(spacer?.style.height) || 1)),
                  containerMask,
                };
              }) || [];
            const changedPosition = Number.isFinite(dirtyFrom) ? dirtyFrom : 0;
            const firstDirtyPage = existingBreakRecords.filter((record) => record.pos < changedPosition).length;

            const mirrorCanvas = document.createElement('div');
            const measurementStartedAt = performance.now();
            mirrorCanvas.className = 'v2-editable-pages';
            mirrorCanvas.dataset.v2PaginationMirror = 'true';
            mirrorCanvas.style.cssText = canvas.style.cssText;
            Object.assign(mirrorCanvas.style, {
              position: 'fixed',
              left: '-100000px',
              top: '0',
              visibility: 'hidden',
              pointerEvents: 'none',
              zIndex: '-1',
            });
            const sourceFrames = canvas.querySelector('.v2-editable-page-frames');
            if (sourceFrames) mirrorCanvas.appendChild(sourceFrames.cloneNode(true));
            document.body.appendChild(mirrorCanvas);

            let mirrorView = null;
            try {
              const cleanState = view.state.apply(
                view.state.tr.setMeta(editablePaginationKey, DecorationSet.empty)
              );
              mirrorView = new EditorView(mirrorCanvas, {
                ...view.props,
                state: cleanState,
                editable: () => false,
                dispatchTransaction: (transaction) => {
                  const nextState = mirrorView.state.apply(transaction);
                  mirrorView.updateState(nextState);
                },
              });

              const mirrorGuides = Array.from(
                mirrorCanvas.querySelectorAll('[data-v2-page-body-guide]')
              );
              const mirrorFrames = Array.from(
                mirrorCanvas.querySelectorAll('.v2-editable-page-frame')
              );
              const breakRecords = existingBreakRecords.slice(0, firstDirtyPage);
              if (breakRecords.length) {
                mirrorView.dispatch(
                  mirrorView.state.tr.setMeta(
                    editablePaginationKey,
                    DecorationSet.create(
                      mirrorView.state.doc,
                      breakRecords.map(createPageBreakDecoration)
                    )
                  )
                );
              }

              // Resolve against the exact preceding layout. Yield after every
              // page so input and painting can continue, and abandon this mirror
              // immediately when a newer document transaction is scheduled.
              for (let index = firstDirtyPage; index < mirrorGuides.length - 1; index += 1) {
                const current = mirrorGuides[index].getBoundingClientRect();
                const next = mirrorGuides[index + 1].getBoundingClientRect();
                const found = findNaturalBreak(
                  mirrorView,
                  current.bottom - PAGE_BODY_EDGE_INSET_PX
                );
                if (!found) break;

                const record = {
                  pos: found.pos,
                  height: Math.max(
                    1,
                    Math.ceil(next.top + PAGE_BODY_EDGE_INSET_PX - found.y)
                  ),
                };
                breakRecords.push(record);
                mirrorView.dispatch(
                  mirrorView.state.tr.setMeta(
                    editablePaginationKey,
                    DecorationSet.create(
                      mirrorView.state.doc,
                      breakRecords.map(createPageBreakDecoration)
                    )
                  )
                );

                const spacer = mirrorView.dom.querySelector(
                  `[data-v2-page-break-index="${index}"]`
                );
                if (spacer instanceof HTMLElement) {
                  record.height = Math.max(
                    1,
                    Math.ceil(
                      next.top + PAGE_BODY_EDGE_INSET_PX -
                      spacer.getBoundingClientRect().top
                    )
                  );
                  spacer.style.height = `${record.height}px`;
                  const spacerRect = spacer.getBoundingClientRect();
                  const spanningContainer = spacer.closest('blockquote, table');
                  if (spanningContainer && mirrorFrames[index] && mirrorFrames[index + 1]) {
                    const containerRect = spanningContainer.getBoundingClientRect();
                    const currentFrame = mirrorFrames[index].getBoundingClientRect();
                    const nextFrame = mirrorFrames[index + 1].getBoundingClientRect();
                    record.containerMask = {
                      left: Math.floor(containerRect.left - spacerRect.left - 2),
                      width: Math.ceil(containerRect.width + 4),
                      pageBottom: Math.max(0, Math.round(currentFrame.bottom - spacerRect.top)),
                      nextPageTop: Math.max(0, Math.round(nextFrame.top - spacerRect.top)),
                    };
                    spacer.classList.add('v2-page-break-spacer--container-mask');
                    spacer.style.setProperty('--v2-break-mask-left', `${record.containerMask.left}px`);
                    spacer.style.setProperty('--v2-break-mask-width', `${record.containerMask.width}px`);
                    spacer.style.setProperty('--v2-break-page-bottom', `${record.containerMask.pageBottom}px`);
                    spacer.style.setProperty('--v2-break-next-page-top', `${record.containerMask.nextPageTop}px`);
                  }
                }

                const previousRecord = existingBreakRecords[index];
                const hasCompleteExistingLayout =
                  existingBreakRecords.length === mirrorGuides.length - 1;
                const boundaryStabilized = canReuseLayout && hasCompleteExistingLayout && previousRecord &&
                  previousRecord.pos === record.pos &&
                  Math.abs(previousRecord.height - record.height) <= 1;
                if (boundaryStabilized) {
                  breakRecords.push(...existingBreakRecords.slice(index + 1));
                  mirrorView.dispatch(
                    mirrorView.state.tr.setMeta(
                      editablePaginationKey,
                      DecorationSet.create(
                        mirrorView.state.doc,
                        breakRecords.map(createPageBreakDecoration)
                      )
                    )
                  );
                  break;
                }

                await new Promise((resolve) => setTimeout(resolve, 0));
                if (destroyed || version !== layoutVersion) return;
              }

              if (destroyed || version !== layoutVersion) return;
              const nextDecorations = DecorationSet.create(
                view.state.doc,
                breakRecords.map(createPageBreakDecoration)
              );
              view.dispatch(view.state.tr.setMeta(editablePaginationKey, nextDecorations));

              const lastContentNode = Array.from(mirrorView.dom.children).at(-1);
              const renderedContentBottom = lastContentNode?.getBoundingClientRect().bottom || mirrorView.dom.getBoundingClientRect().top;
              const lastGuide = mirrorGuides[mirrorGuides.length - 1].getBoundingClientRect();
              const requiredPageCount = renderedContentBottom > lastGuide.bottom - PAGE_BODY_EDGE_INSET_PX
                ? mirrorGuides.length + 1
                : Math.max(1, breakRecords.length + 1);
              canvas.dispatchEvent(new CustomEvent('v2surfacepagecount', { detail: requiredPageCount }));
              dirtyFrom = null;
              canReuseLayout = true;
              canvas.dataset.v2PaginationCompletedDurationMs = String(
                Math.round(performance.now() - measurementStartedAt)
              );
              canvas.dataset.v2PaginationRevision = String(
                Number(canvas.dataset.v2PaginationRevision || 0) + 1
              );
            } catch (error) {
              console.error('V2 pagination mirror failed:', error);
            } finally {
              mirrorView?.destroy();
              mirrorCanvas.remove();
              canvas.dataset.v2PaginationDurationMs = String(
                Math.round(performance.now() - measurementStartedAt)
              );
            }
          };
          const schedule = (delay = 0) => {
            clearTimer();
            const version = ++layoutVersion;
            const requestMeasure = () => {
              delayTimer = null;
              frame = requestAnimationFrame(() => measure(version));
            };
            if (delay > 0) delayTimer = setTimeout(requestMeasure, delay);
            else requestMeasure();
          };
          const onResize = () => {
            dirtyFrom = 0;
            canReuseLayout = false;
            schedule(50);
          };
          schedule();
          window.addEventListener('resize', onResize);
          document.fonts?.ready.then(() => {
            if (!destroyed) onResize();
          });
          const mutationObserver = new MutationObserver(() => {
            dirtyFrom = 0;
            canReuseLayout = false;
            schedule();
          });
          const canvas = view.dom.closest('.v2-editable-pages');
          const frameHost = canvas?.querySelector('.v2-editable-page-frames');
          if (frameHost) mutationObserver.observe(frameHost, { childList: true });
          return {
            update: (nextView, previousState) => {
              view = nextView;
              if (nextView.state.doc !== previousState.doc) {
                const changedFrom = previousState.doc.content.findDiffStart(nextView.state.doc.content);
                if (Number.isFinite(changedFrom)) {
                  dirtyFrom = dirtyFrom == null ? changedFrom : Math.min(dirtyFrom, changedFrom);
                }
                schedule(180);
              }
            },
            destroy: () => {
              destroyed = true;
              layoutVersion += 1;
              clearTimer();
              mutationObserver.disconnect();
              window.removeEventListener('resize', onResize);
            },
          };
        },
      }),
    ];
  },
});

const SAFE_TEXT_SPLIT_TAGS = new Set([
  'P',
  'LI',
  'TD',
  'TH',
  'BLOCKQUOTE',
  'PRE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
]);

const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderInlineMarkdown = (line) => {
  const math = [];
  const protectedLine = String(line || '').replace(/(?<!\$)\$([^$\n]+)\$(?!\$)/g, (_, latex) => {
    const token = `V2INLINEMATH${math.length}TOKEN`;
    math.push(latex);
    return token;
  });
  let out = escapeHtml(protectedLine);
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
  math.forEach((latex, index) => {
    out = out.replace(
      `V2INLINEMATH${index}TOKEN`,
      `<span data-type="inline-math" data-latex="${escapeHtml(latex)}"></span>`
    );
  });
  return out;
};

const isTableSeparatorRow = (line) => /^\s*\|?[\s:-]+(\|[\s:-]+)+\|?\s*$/.test(line);
const isMarkdownDivider = (line) => /^(?:-{3,}|\*{3,}|_{3,})$/.test(String(line || '').trim());
const getListMarker = (line) => String(line || '').trim().match(/^(\*|-|\+|(\d+(?:\.\d+)*)\.)(?:\s+(.*)|\s*)$/);
const isOrderedListMarker = (line) => {
  const match = getListMarker(line);
  return Boolean(match?.[2]);
};

const splitPipeCells = (line) => {
  let text = String(line || '').trim();
  if (text.startsWith('|')) text = text.slice(1);
  if (text.endsWith('|')) text = text.slice(0, -1);
  return text.split('|').map((c) => c.trim());
};

const findPipeTableHeader = (lines, startIndex) => {
  if (!lines[startIndex]?.trim().startsWith('|')) return null;

  const headerCells = [];
  for (let index = startIndex; index < lines.length; index += 1) {
    const line = lines[index].trim();
    if (!line) continue;

    if (index > startIndex && isTableSeparatorRow(line)) {
      const columnCount = splitPipeCells(line).length;
      if (columnCount < 2 || headerCells.length !== columnCount) return null;
      return {
        cells: headerCells,
        columnCount,
        separatorIndex: index,
      };
    }

    if (!line.startsWith('|')) return null;
    const cells = splitPipeCells(line).filter((cell) => cell.length > 0);
    headerCells.push(...cells);
  }

  return null;
};

const fallbackMarkdownToHtml = (text) => {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i += 1;
      continue;
    }
    const codeFence = line.match(/^```([\w-]*)\s*$/);
    if (codeFence) {
      const code = [];
      i += 1;
      while (i < lines.length && !/^```\s*$/.test(lines[i].trim())) {
        code.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) i += 1;
      const languageClass = codeFence[1] ? ` class="language-${escapeHtml(codeFence[1])}"` : '';
      out.push(`<pre><code${languageClass}>${escapeHtml(code.join('\n'))}</code></pre>`);
      continue;
    }
    const displayMath = line.match(/^\$\$([\s\S]+)\$\$$/);
    if (displayMath) {
      out.push(`<div data-type="block-math" data-latex="${escapeHtml(displayMath[1].trim())}"></div>`);
      i += 1;
      continue;
    }
    if (isMarkdownDivider(line)) {
      out.push('<hr>');
      i += 1;
      continue;
    }
    if (/^>\s?/.test(line)) {
      const quoteLines = [];
      while (i < lines.length) {
        const candidate = lines[i].trim();
        if (!/^>\s?/.test(candidate)) break;
        const unwrapped = candidate
          .replace(/^>\s?/, '')
          .split(/\s+>\s*/)
          .map((part) => part.trim());
        quoteLines.push(...unwrapped);
        i += 1;
      }
      out.push(`<blockquote>${fallbackMarkdownToHtml(quoteLines.join('\n'))}</blockquote>`);
      continue;
    }
    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      out.push(`<h${level}>${renderInlineMarkdown(heading[2])}</h${level}>`);
      i += 1;
      continue;
    }
    const tableHeader = findPipeTableHeader(lines, i);
    if (tableHeader) {
      const head = tableHeader.cells;
      i = tableHeader.separatorIndex + 1;
      const rows = [];
      while (i < lines.length) {
        while (i < lines.length && !lines[i].trim()) i += 1;
        if (i >= lines.length || !lines[i].trim().startsWith('|')) break;
        const cells = [];
        while (i < lines.length && cells.length < head.length) {
          const candidate = lines[i].trim();
          if (!candidate) {
            i += 1;
            continue;
          }
          if (!candidate.startsWith('|')) break;
          const parts = splitPipeCells(candidate);
          if (parts.some((part) => part.length > 0)) cells.push(...parts);
          i += 1;
        }
        if (cells.length) rows.push(cells.slice(0, head.length));
      }
      out.push(
        `<table><thead><tr>${head.map((c) => `<th>${renderInlineMarkdown(c)}</th>`).join('')}</tr></thead><tbody>${rows
          .map((r) => `<tr>${head.map((_, index) => `<td>${renderInlineMarkdown(r[index] || '')}</td>`).join('')}</tr>`)
          .join('')}</tbody></table>`
      );
      continue;
    }
    if (isOrderedListMarker(line)) {
      const items = [];
      while (i < lines.length && isOrderedListMarker(lines[i].trim())) {
        const match = getListMarker(lines[i]);
        items.push({ label: match?.[2] || '', content: match?.[3] || '' });
        i += 1;
      }
      if (items.some((item) => item.label.includes('.'))) {
        out.push(items.map((item) => `<p><strong>${escapeHtml(item.label)}.</strong> ${renderInlineMarkdown(item.content)}</p>`).join(''));
      } else {
        out.push(`<ol>${items.map((item) => `<li>${renderInlineMarkdown(item.content)}</li>`).join('')}</ol>`);
      }
      continue;
    }
    if (/^[*+-](?:\s+|$)/.test(line)) {
      const items = [];
      while (i < lines.length) {
        while (i < lines.length && !lines[i].trim()) i += 1;
        const match = getListMarker(lines[i]);
        if (!match || match[2]) break;
        let item = match[3] || '';
        i += 1;
        while (!item && i < lines.length && !lines[i].trim()) i += 1;
        if (!item && i < lines.length && !getListMarker(lines[i])) {
          item = lines[i].trim();
          i += 1;
        }
        items.push(item);
      }
      out.push(`<ul>${items.map((x) => `<li>${renderInlineMarkdown(x)}</li>`).join('')}</ul>`);
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim()) {
      const candidate = lines[i].trim();
      if (isMarkdownDivider(candidate)) break;
      if (/^```/.test(candidate)) break;
      if (/^>\s?/.test(candidate)) break;
      if (/^(#{1,6})\s+/.test(candidate)) break;
      if (isOrderedListMarker(candidate)) break;
      if (/^[*+-](?:\s+|$)/.test(candidate)) break;
      if (findPipeTableHeader(lines, i)) break;
      para.push(candidate);
      i += 1;
    }
    out.push(`<p>${renderInlineMarkdown(para.join(' '))}</p>`);
  }
  return out.join('') || '<p><br></p>';
};

const hasPipeTable = (text) => {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  return lines.some((_, index) => Boolean(findPipeTableHeader(lines, index)));
};

const normalizeMultilinePipeTables = (text) => {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const tableHeader = findPipeTableHeader(lines, i);
    if (!tableHeader) {
      out.push(lines[i]);
      i += 1;
      continue;
    }

    const header = tableHeader.cells;
    out.push(`| ${header.join(' | ')} |`);
    out.push(lines[tableHeader.separatorIndex]);
    i = tableHeader.separatorIndex + 1;

    while (i < lines.length) {
      const leadingBlanks = [];
      while (i < lines.length && !lines[i].trim()) {
        leadingBlanks.push(lines[i]);
        i += 1;
      }
      if (i >= lines.length || !lines[i].trim().startsWith('|')) {
        out.push(...(leadingBlanks.length ? leadingBlanks : ['']));
        break;
      }

      const cells = [];
      while (i < lines.length && cells.length < header.length) {
        if (!lines[i].trim()) {
          i += 1;
          continue;
        }
        if (!lines[i].trim().startsWith('|')) break;
        const parts = splitPipeCells(lines[i]);
        if (parts.some((part) => part.length > 0)) cells.push(...parts);
        i += 1;
      }

      if (cells.length) {
        out.push(`| ${header.map((_, index) => cells[index] || '').join(' | ')} |`);
      }
    }
  }
  return out.join('\n');
};

const splitBlockquotesForPagination = (container) => {
  Array.from(container.querySelectorAll('blockquote')).reverse().forEach((quote) => {
    const children = Array.from(quote.children);
    if (children.length <= 1) return;
    const fragments = children.map((child) => {
      const fragment = quote.cloneNode(false);
      fragment.appendChild(child.cloneNode(true));
      return fragment;
    });
    quote.replaceWith(...fragments);
  });
};

const markdownToHtml = (text) => {
  const markdownSource = hasPipeTable(text)
    ? normalizeMultilinePipeTables(text)
    : String(text || '');
  const withMathNodes = markdownSource
    .replace(/\$\$([\s\S]+?)\$\$/g, (_, latex) =>
      `\n<div data-type="block-math" data-latex="${escapeHtml(latex.trim())}"></div>\n`
    )
    .replace(/(?<!\$)\$([^$\n]+)\$(?!\$)/g, (_, latex) =>
      `<span data-type="inline-math" data-latex="${escapeHtml(latex.trim())}"></span>`
    );

  try {
    const container = document.createElement('div');
    container.innerHTML = marked.parse(withMathNodes, { gfm: true, breaks: false });
    Array.from(container.querySelectorAll('blockquote')).reverse().forEach((quote) => {
      const hasContent = (quote.textContent || '').trim() ||
        quote.querySelector('img, table, pre, hr, [data-type="inline-math"], [data-type="block-math"]');
      if (!hasContent) quote.remove();
    });
    splitBlockquotesForPagination(container);
    return container.innerHTML || '<p><br></p>';
  } catch (_) {
    return fallbackMarkdownToHtml(text);
  }
};

const hasDollarDelimitedMath = (text) =>
  /\$\$[\s\S]+?\$\$|(?<!\$)\$[^$\n]+\$(?!\$)/.test(String(text || ''));

// Tiptap's default migration regex treats `$$latex$$` as `$latex` followed by
// a stray closing `$`. Keep the migration as a compatibility fallback for
// saved inline math, but never let it consume part of a display-math pair.
const migrateInlineMathStrings = (editor) => migrateMathStrings(
  editor,
  /(?<!\$)\$(?!\$|\d+\$)([^$\n]+?)\$(?!\$|\d)/g
);

const getMarkdownTextFromElement = (element) => {
  const clone = element.cloneNode(true);
  clone.querySelectorAll('br').forEach((br) => br.replaceWith(document.createTextNode('\n')));
  return (clone.textContent || '').replace(/\u00a0/g, ' ').trim();
};

const replaceRenderedMarkdownTables = (container) => {
  // Some saved/generated content has already been "rendered" into one paragraph
  // per Markdown line (or a single paragraph containing <br>s). Turn those pipe
  // rows back into real table elements before ProseMirror parses the HTML. A real
  // table is then represented by TableKit nodes and can be edited by the toolbar.
  container.querySelectorAll('p').forEach((paragraph) => {
    if (!paragraph.isConnected || !paragraph.querySelector('br')) return;
    const markdown = getMarkdownTextFromElement(paragraph);
    if (!hasPipeTable(markdown)) return;

    const replacement = document.createElement('div');
    replacement.innerHTML = markdownToHtml(markdown);
    const table = replacement.querySelector('table');
    if (table) paragraph.replaceWith(...Array.from(replacement.childNodes));
  });

  const parents = [container, ...Array.from(container.querySelectorAll('div, blockquote, li'))];
  parents.forEach((parent) => {
    let child = parent.firstElementChild;
    while (child) {
      const header = child;
      const headerText = header.tagName === 'P' ? getMarkdownTextFromElement(header) : '';
      if (!headerText.startsWith('|')) {
        child = child.nextElementSibling;
        continue;
      }

      const tableElements = [];
      const headerLines = [];
      let candidate = header;
      let tableHeader = null;
      while (candidate?.tagName === 'P') {
        const line = getMarkdownTextFromElement(candidate);
        if (line && !line.startsWith('|')) break;
        tableElements.push(candidate);
        headerLines.push(line);
        candidate = candidate.nextElementSibling;
        tableHeader = findPipeTableHeader(headerLines, 0);
        if (tableHeader) break;
      }

      if (!tableHeader) {
        child = child.nextElementSibling;
        continue;
      }

      while (candidate?.tagName === 'P') {
        const line = getMarkdownTextFromElement(candidate);
        if (line && !line.startsWith('|')) break;
        tableElements.push(candidate);
        candidate = candidate.nextElementSibling;
      }

      const markdown = tableElements.map(getMarkdownTextFromElement).join('\n');
      const replacement = document.createElement('div');
      replacement.innerHTML = markdownToHtml(markdown);
      const table = replacement.querySelector('table');
      if (!table) {
        child = candidate;
        continue;
      }

      header.replaceWith(table);
      tableElements.slice(1).forEach((element) => element.remove());
      child = candidate;
    }
  });
};

const normalizePlainTextToHtml = (text) => {
  const normalized = String(text || '').replace(/\r\n?/g, '\n');
  const looksLikeMarkdown =
    /(^|\n)\s*#{1,6}\s+/.test(normalized) ||
    /(^|\n)\s*[*-]\s+/.test(normalized) ||
    /(^|\n)\s*\d+\.\s+/.test(normalized) ||
    /(^|\n)\s*\|.+\|/.test(normalized) ||
    /(^|\n)[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*(?=\n|$)/.test(normalized) ||
    /\*\*[^*]+\*\*/.test(normalized) ||
    hasDollarDelimitedMath(normalized);
  if (looksLikeMarkdown) return markdownToHtml(normalized);
  const blocks = normalized.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  if (!blocks.length) return '<p><br></p>';
  return blocks.map((block) => `<p>${escapeHtml(block)}</p>`).join('');
};

const isEmptyPageBoundaryParagraph = (node) => {
  if (!(node instanceof HTMLElement) || node.tagName !== 'P') return false;
  const clone = node.cloneNode(true);
  clone.querySelectorAll('br, .zws, [data-zws], [data-cursor-marker]').forEach((child) => child.remove());
  if ((clone.textContent || '').replace(/[\s\u00A0\u200B]/g, '')) return false;
  return !clone.querySelector(
    'img, table, hr, audio, video, iframe, .math-container, .image-container, ' +
    '[data-type="inline-math"], [data-type="block-math"], .response-area-block, ' +
    '.document-step-block, .section-divider-block'
  );
};

const removePageBoundaryPlaceholderParagraphs = (bodyPages) => {
  const pages = Array.isArray(bodyPages) ? bodyPages : [];
  return pages.map((html, pageIndex) => {
    const container = document.createElement('div');
    container.innerHTML = html || '';

    // Legacy pagination left caret-only paragraphs at page edges. Once pages
    // are recombined into V2's continuous model those placeholders become
    // visible blank paragraphs, so discard them only at an internal seam.
    if (pageIndex > 0) {
      while (isEmptyPageBoundaryParagraph(container.firstElementChild)) {
        container.firstElementChild.remove();
      }
    }
    if (pageIndex < pages.length - 1) {
      while (isEmptyPageBoundaryParagraph(container.lastElementChild)) {
        container.lastElementChild.remove();
      }
    }
    return container.innerHTML;
  });
};

const collapseRepeatedEmptyParagraphs = (container) => {
  const parents = [container, ...Array.from(container.querySelectorAll('blockquote, li, td, th'))];
  parents.forEach((parent) => {
    let previousWasEmpty = false;
    Array.from(parent.children).forEach((child) => {
      const isEmpty = isEmptyPageBoundaryParagraph(child);
      if (isEmpty && previousWasEmpty) {
        child.remove();
        return;
      }
      previousWasEmpty = isEmpty;
    });
  });
};

const canonicalizeEmptyParagraphs = (container) => {
  container.querySelectorAll('p').forEach((paragraph) => {
    if (!isEmptyPageBoundaryParagraph(paragraph)) return;
    paragraph.querySelectorAll('br, .zws, [data-zws], [data-cursor-marker]')
      .forEach((node) => node.remove());
  });
};

const normalizeLoadedBodyHtml = (bodyHtml) => {
  const raw = String(bodyHtml || '');
  if (!raw.trim()) return '<p><br></p>';
  const container = document.createElement('div');
  container.innerHTML = raw;
  const hasElements = Array.from(container.childNodes).some((n) => n.nodeType === Node.ELEMENT_NODE);
  if (hasElements) {
    // This break is a ProseMirror DOM caret aid, not document content. Older
    // projections saved it into HTML; parsing that HTML turns it into a real
    // hard break and ProseMirror then adds another caret aid on every reload.
    container.querySelectorAll('br.ProseMirror-trailingBreak').forEach((node) => node.remove());
    replaceRenderedMarkdownTables(container);
    container.querySelectorAll('p').forEach((paragraph) => {
      if (isMarkdownDivider(paragraph.textContent)) {
        paragraph.replaceWith(document.createElement('hr'));
        return;
      }

      const source = paragraph.cloneNode(true);
      source.querySelectorAll('[data-type="inline-math"], [data-type="block-math"]').forEach((mathNode) => {
        const latex = mathNode.getAttribute('data-latex') || '';
        mathNode.replaceWith(document.createTextNode(`$${latex}$`));
      });
      source.querySelectorAll('.math-container[data-math-content]').forEach((mathNode) => {
        const latex = mathNode.getAttribute('data-math-content') || '';
        mathNode.replaceWith(document.createTextNode(`$${latex}$`));
      });
      const markdownSource = (source.textContent || '').trim();
      if (!markdownSource.startsWith('>')) return;

      const replacement = document.createElement('div');
      replacement.innerHTML = markdownToHtml(markdownSource);
      paragraph.replaceWith(...Array.from(replacement.childNodes));
    });
    Array.from(container.querySelectorAll('blockquote')).reverse().forEach((quote) => {
      const hasContent = (quote.textContent || '').trim() ||
        quote.querySelector('img, table, pre, hr, [data-type="inline-math"], [data-type="block-math"]');
      if (!hasContent) quote.remove();
    });
    splitBlockquotesForPagination(container);
    canonicalizeEmptyParagraphs(container);
    collapseRepeatedEmptyParagraphs(container);
    return container.innerHTML;
  }
  const text = container.textContent || raw;
  return normalizePlainTextToHtml(text);
};

const normalizeMathForModel = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html || '<p><br></p>';
  container.querySelectorAll('p').forEach((paragraph) => {
    if (paragraph.querySelector('[data-type="inline-math"], [data-type="block-math"], .math-container')) return;
    const match = (paragraph.textContent || '').trim().match(/^\$\$([^$\n]+)\$\$$/);
    if (!match) return;
    const replacement = document.createElement('div');
    replacement.setAttribute('data-type', 'block-math');
    replacement.setAttribute('data-latex', match[1].trim());
    paragraph.replaceWith(replacement);
  });
  container.querySelectorAll('.math-container[data-math-content]').forEach((node) => {
    const latex = node.getAttribute('data-math-content') || '';
    const isBlock = node.getAttribute('data-math-display') === 'block' || node.classList.contains('display-math');
    const replacement = document.createElement(isBlock ? 'div' : 'span');
    replacement.setAttribute('data-type', isBlock ? 'block-math' : 'inline-math');
    replacement.setAttribute('data-latex', latex);
    node.replaceWith(replacement);
  });
  return container.innerHTML || '<p><br></p>';
};

const hasMeaningfulHtml = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html || '';
  if (container.querySelector('img, table, hr, audio, video, iframe, .section-divider-block, [data-type="inline-math"], [data-type="block-math"]')) {
    return true;
  }
  return (container.textContent || '').replace(/\u200B/g, '').trim().length > 0;
};

const materializeMathForProjection = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html || '<p><br></p>';
  container.querySelectorAll('[data-type="inline-math"], [data-type="block-math"]').forEach((node) => {
    const latex = node.getAttribute('data-latex') || '';
    const isBlock = node.getAttribute('data-type') === 'block-math';
    const replacement = document.createElement(isBlock ? 'div' : 'span');
    replacement.className = isBlock ? 'math-container display-math' : 'math-container';
    replacement.setAttribute('data-math-content', latex);
    if (isBlock) replacement.setAttribute('data-math-display', 'block');
    try {
      replacement.innerHTML = katex.renderToString(latex, {
        ...KATEX_OPTIONS,
        displayMode: isBlock,
        output: 'html',
      });
    } catch (_) {
      replacement.textContent = latex;
    }
    node.replaceWith(replacement);
  });
  container.querySelectorAll('.document-step-audio-block').forEach((node) => {
    node.innerHTML = buildV2StepAudioHtml({
      url: node.getAttribute('data-step-audio-url') || node.querySelector('audio')?.getAttribute('src') || '',
      name: node.getAttribute('data-step-audio-name') || 'Step audio',
      contentType: node.getAttribute('data-step-audio-type') || 'audio/webm',
      path: node.getAttribute('data-step-audio-path') || '',
      duration: Number(node.getAttribute('data-step-audio-duration') || 0),
    });
  });
  container.querySelectorAll('.document-step-embed-block').forEach((node) => {
    const attrs = {
      kind: node.getAttribute('data-step-embed-kind') || 'html',
      src: node.getAttribute('data-embed-src') || '',
      html: node.getAttribute('data-embed-html') || '',
    };
    node.innerHTML = buildV2StepEmbedHtml(attrs);
    const step = node.closest('.document-step-block');
    step?.querySelectorAll(
      '[data-step-action="html"], [data-step-action="url"], [data-step-action="youtube"]'
    ).forEach((button) => {
      button.style.display = 'none';
    });
  });
  return container.innerHTML || '<p><br></p>';
};

const getImageDimensions = (src) => new Promise((resolve) => {
  const probe = new Image();
  probe.onload = () => resolve({ naturalWidth: probe.naturalWidth || null, naturalHeight: probe.naturalHeight || null });
  probe.onerror = () => resolve({ naturalWidth: null, naturalHeight: null });
  probe.src = src;
});

const uploadDocumentImage = async (blob, userId, originalName = 'pasted-image.png') => {
  if (!userId) throw new Error('Sign in before inserting an image.');
  const safeName = String(originalName || 'pasted-image.png').replace(/[^a-zA-Z0-9._-]/g, '_');
  const uniquePart = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const target = storageRef(storage, `sigma-images/${userId}/${uniquePart}_${safeName}`);
  const snapshot = await uploadBytes(target, blob);
  return getDownloadURL(snapshot.ref);
};

const persistImageSource = async (src, userId, originalName) => {
  if (!String(src || '').startsWith('data:image/')) return src;
  const response = await fetch(src);
  const blob = await response.blob();
  return uploadDocumentImage(blob, userId, originalName);
};

const readClipboardImage = async (file, userId) => {
  const src = await uploadDocumentImage(file, userId, file?.name || 'pasted-image.png');
  return { src, ...(await getImageDimensions(src)) };
};

const persistEditorDataImages = async (editor, userId) => {
  if (!editor || editor.isDestroyed) return false;
  const pending = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'resizableImage' && String(node.attrs.src || '').startsWith('data:image/')) {
      pending.push({ node, pos });
    }
  });
  if (!pending.length) return false;

  const uploaded = await Promise.all(pending.map(async ({ node, pos }, index) => ({
    pos,
    attrs: {
      ...node.attrs,
      src: await persistImageSource(node.attrs.src, userId, node.attrs.alt || `pasted-image-${index + 1}.png`),
    },
  })));
  let transaction = editor.state.tr;
  uploaded.forEach(({ pos, attrs }) => {
    const current = transaction.doc.nodeAt(pos);
    if (current?.type.name === 'resizableImage') {
      transaction = transaction.setNodeMarkup(pos, undefined, attrs);
    }
  });
  if (!transaction.docChanged) return false;
  editor.view.dispatch(transaction);
  return true;
};

const persistHtmlDataImages = async (html, userId) => {
  if (!String(html || '').includes('data:image/')) return html;
  const container = document.createElement('div');
  container.innerHTML = html || '';
  const images = Array.from(container.querySelectorAll('img[src^="data:image/"]'));
  await Promise.all(images.map(async (image, index) => {
    const src = image.getAttribute('src') || '';
    const nextSrc = await persistImageSource(
      src,
      userId,
      image.getAttribute('alt') || `document-image-${index + 1}.png`
    );
    image.setAttribute('src', nextSrc);
  }));
  return container.innerHTML;
};

const EditablePageRegion = ({
  className,
  html,
  regionClass,
  editable,
  onFocus,
  onInput,
  onBlur,
  onPaste,
  onClick,
}) => {
  const nodeRef = useRef(null);
  const lastAppliedHtmlRef = useRef(null);
  const normalizedHtml = html || '';

  const syncHtml = useCallback((node) => {
    if (!node) return;
    const active = document.activeElement;
    if (active === node || node.contains(active)) return;
    if (lastAppliedHtmlRef.current === normalizedHtml) return;
    node.innerHTML = normalizedHtml;
    lastAppliedHtmlRef.current = normalizedHtml;
  }, [normalizedHtml]);

  const setNodeRef = useCallback((node) => {
    nodeRef.current = node;
    syncHtml(node);
  }, [syncHtml]);

  useEffect(() => {
    syncHtml(nodeRef.current);
  }, [syncHtml]);

  const handleInput = useCallback((event) => {
    lastAppliedHtmlRef.current = event.currentTarget.innerHTML;
    onInput?.(regionClass, event);
  }, [onInput, regionClass]);

  const handleBlur = useCallback((event) => {
    lastAppliedHtmlRef.current = event.currentTarget.innerHTML;
    onBlur?.(regionClass, event);
  }, [onBlur, regionClass]);

  const handleFocus = useCallback((event) => {
    onFocus?.(regionClass, event);
  }, [onFocus, regionClass]);

  return (
    <div
      ref={setNodeRef}
      className={className}
      contentEditable={editable}
      suppressContentEditableWarning
      onFocus={handleFocus}
      onInput={handleInput}
      onBlur={handleBlur}
      onPaste={onPaste}
      onClick={onClick}
    />
  );
};

const getPageRegions = (pageHtml) => {
  const c = document.createElement('div');
  c.innerHTML = pageHtml || '';
  const header = c.querySelector('.doc-header');
  const footer = c.querySelector('.doc-footer');
  const headerHtml = header ? header.outerHTML : '';
  const footerHtml = footer ? footer.outerHTML : '';
  if (header) header.remove();
  if (footer) footer.remove();
  return { headerHtml, footerHtml, bodyHtml: c.innerHTML };
};

const getPageNumberStart = (pageNumber, fallback = 1) => {
  const parsed = Number.parseInt(pageNumber?.getAttribute('data-page-number-start') || fallback, 10);
  return Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
};

const getPageNumberText = (pageIndex, totalPages, startNumber = 1) =>
  `Page ${startNumber + pageIndex} of ${startNumber + totalPages - 1}`;

const renderRegionPageNumber = (regionHtml, pageIndex, totalPages) => {
  if (!regionHtml) return '';
  const container = document.createElement('div');
  container.innerHTML = regionHtml;
  const pageNumber = container.querySelector('.page-number[data-page-number]');
  if (pageNumber) {
    const startNumber = getPageNumberStart(pageNumber);
    pageNumber.textContent = getPageNumberText(pageIndex, totalPages, startNumber);
  }
  return container.innerHTML;
};

const attachPageRegionMetrics = (pageHtml, { topSpacePx, bottomSpacePx }) => {
  const container = document.createElement('div');
  container.innerHTML = pageHtml || '';
  const header = container.querySelector('.doc-header');
  const footer = container.querySelector('.doc-footer');
  if (header) header.setAttribute('data-v2-region-space-px', String(Math.max(REGION_MIN_SPACE_PX, Math.ceil(topSpacePx))));
  if (footer) footer.setAttribute('data-v2-region-space-px', String(Math.max(REGION_MIN_SPACE_PX, Math.ceil(bottomSpacePx))));
  return container.innerHTML;
};

const upsertFooterPageNumber = (pageHtml, align, pageIndex, totalPages, startNumber = 1) => {
  const container = document.createElement('div');
  container.innerHTML = pageHtml || '';
  let footer = container.querySelector('.doc-footer');
  if (!footer) {
    footer = document.createElement('div');
    footer.className = 'doc-footer';
    footer.setAttribute('data-doc-footer', 'true');
    container.appendChild(footer);
  }
  footer.querySelectorAll('.doc-region-placeholder').forEach((placeholder) => placeholder.remove());
  let pageNumber = footer.querySelector('.page-number[data-page-number]');
  if (!pageNumber) {
    pageNumber = document.createElement('div');
    pageNumber.setAttribute('data-page-number', 'true');
    footer.appendChild(pageNumber);
  }
  const normalizedStart = getPageNumberStart(null, startNumber);
  pageNumber.className = `page-number align-${align}`;
  pageNumber.setAttribute('data-page-number-start', String(normalizedStart));
  pageNumber.textContent = getPageNumberText(pageIndex, totalPages, normalizedStart);
  return container.innerHTML;
};

const getCommonRegions = (pages) => {
  let headerHtml = '';
  let footerHtml = '';
  for (const html of pages || []) {
    const pageRegions = getPageRegions(html || '');
    if (!headerHtml && pageRegions.headerHtml) headerHtml = pageRegions.headerHtml;
    if (!footerHtml && pageRegions.footerHtml) footerHtml = pageRegions.footerHtml;
    if (headerHtml && footerHtml) break;
  }
  return { headerHtml, footerHtml };
};

const doesHeaderRepeat = (headerHtml) => {
  if (!headerHtml || typeof document === 'undefined') return false;
  const container = document.createElement('div');
  container.innerHTML = headerHtml;
  return container.querySelector('.doc-header')?.getAttribute('data-header-repeat') === 'all';
};

const measureRegionSpacePx = (regionHtml) => {
  if (typeof document === 'undefined' || !regionHtml) return REGION_MIN_SPACE_PX;
  const host = document.createElement('div');
  host.style.cssText = `position:absolute;left:-9999px;top:0;visibility:hidden;width:${BODY_WIDTH_STYLE};box-sizing:border-box;`;
  host.innerHTML = regionHtml;
  host.querySelectorAll('.doc-header, .doc-footer').forEach((el) => {
    el.style.position = 'static';
    el.style.left = 'auto';
    el.style.right = 'auto';
    el.style.top = 'auto';
    el.style.bottom = 'auto';
    el.style.margin = '0';
  });
  document.body.appendChild(host);
  try {
    return Math.max(REGION_MIN_SPACE_PX, Math.ceil(host.scrollHeight || 0));
  } finally {
    document.body.removeChild(host);
  }
};

const createMeasureHost = ({ topSpacePx, bottomSpacePx }) => {
  const host = document.createElement('div');
  host.className = 'v2-model-editor-content v2-pagination-measure-host';
  host.style.cssText =
    'position:absolute;left:-9999px;top:0;visibility:hidden;' +
    `width:${BODY_WIDTH_STYLE};height:${getBodyHeightStyle(topSpacePx, bottomSpacePx)};` +
    'min-height:0;padding:0;box-sizing:border-box;overflow:hidden;font-size:14px;line-height:1.6;font-family:Inter,system-ui,sans-serif;';
  document.body.appendChild(host);
  return host;
};

const setMeasureHostPageRegionSpaces = (host, regionSpaces, pageIndex) => {
  const topSpacePx = pageIndex === 0
    ? regionSpaces.topSpacePx
    : (regionSpaces.subsequentTopSpacePx ?? regionSpaces.topSpacePx);
  host.style.height = getBodyHeightStyle(topSpacePx, regionSpaces.bottomSpacePx);
};

const parseHtmlNodes = (html) => {
  const c = document.createElement('div');
  c.innerHTML = html || '';
  return Array.from(c.childNodes).map((n) => n.cloneNode(true));
};

const serializeNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.textContent || '');
  if (node.nodeType === Node.ELEMENT_NODE) return node.outerHTML;
  return '';
};

const serializeDomFragment = (fragment) => {
  const host = document.createElement('div');
  host.appendChild(fragment);
  host.querySelectorAll(
    '.v2-page-break-spacer, .v2-image-controls, .v2-image-resize-handle, .column-resize-handle, ' +
    '.math-toolbar, .math-edit-overlay, br.ProseMirror-trailingBreak'
  ).forEach((node) => node.remove());
  host.querySelectorAll('[contenteditable], [draggable], [spellcheck]').forEach((node) => {
    node.removeAttribute('contenteditable');
    node.removeAttribute('draggable');
    node.removeAttribute('spellcheck');
  });
  host.querySelectorAll('.v2-image-node').forEach((node) => {
    node.classList.remove('v2-image-node', 'v2-image-selected', 'ProseMirror-selectednode');
  });
  host.querySelectorAll('.selectedCell').forEach((node) => {
    node.classList.remove('selectedCell');
  });
  host.querySelectorAll('.tableWrapper').forEach((wrapper) => {
    const table = wrapper.querySelector(':scope > table');
    if (table) wrapper.replaceWith(table);
  });
  host.querySelectorAll('img').forEach((img) => {
    img.style.height = 'auto';
    img.style.maxWidth = '100%';
  });
  return host.innerHTML;
};

const PROJECTION_LIST_ID_ATTRIBUTE = 'data-v2-projection-list-id';
const PROJECTION_LIST_ITEM_ID_ATTRIBUTE = 'data-v2-projection-list-item-id';
const PROJECTION_TEXT_BLOCK_ID_ATTRIBUTE = 'data-v2-projection-text-block-id';
const SAVED_TEXT_BLOCK_ID_ATTRIBUTE = 'data-v2-saved-text-block-id';
const SAVED_TEXT_BLOCK_CONTINUATION_ATTRIBUTE = 'data-v2-text-block-continuation';
const PROJECTED_TEXT_BLOCK_SELECTOR = ':scope > p, :scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6, :scope > pre';
let fallbackTextBlockId = 0;

const hasMeaningfulListItemContent = (item) => {
  if ((item.textContent || '').replace(/\u200B/g, '').trim()) return true;
  return Boolean(item.querySelector(
    'img, table, hr, audio, video, iframe, ol, ul, .image-container, .math-container, ' +
    '[data-type="inline-math"], [data-type="block-math"], .section-divider-block'
  ));
};

const normalizeProjectedListPages = (pages) => {
  const containers = pages.map((html) => {
    const container = document.createElement('div');
    container.innerHTML = html || '';
    return container;
  });
  const occurrencesByListId = new Map();

  containers.forEach((container) => {
    container.querySelectorAll(`[${PROJECTION_LIST_ID_ATTRIBUTE}]`).forEach((list) => {
      const listId = list.getAttribute(PROJECTION_LIST_ID_ATTRIBUTE);
      if (!listId) return;
      const occurrences = occurrencesByListId.get(listId) || [];
      occurrences.push(list);
      occurrencesByListId.set(listId, occurrences);
    });
  });

  occurrencesByListId.forEach((occurrences) => {
    occurrences.forEach((list, occurrenceIndex) => {
      const directItems = () =>
        Array.from(list.children).filter((child) => child.tagName === 'LI');

      if (occurrenceIndex > 0) {
        let firstItem = directItems()[0];
        while (firstItem && !hasMeaningfulListItemContent(firstItem)) {
          firstItem.remove();
          firstItem = directItems()[0];
        }
      }
      if (occurrenceIndex < occurrences.length - 1) {
        let items = directItems();
        let lastItem = items.at(-1);
        while (lastItem && !hasMeaningfulListItemContent(lastItem)) {
          lastItem.remove();
          items = directItems();
          lastItem = items.at(-1);
        }
      }
    });

    const ordered = occurrences[0]?.tagName === 'OL';
    let nextValue = ordered ? Number(occurrences[0].getAttribute('start')) || 1 : null;
    const logicalValuesByItemId = new Map();
    const seenItemIds = new Set();
    occurrences.forEach((list, occurrenceIndex) => {
      if (ordered && occurrenceIndex > 0) list.setAttribute('start', String(nextValue));
      Array.from(list.children)
        .filter((child) => child.tagName === 'LI')
        .forEach((item) => {
          const itemId = item.getAttribute(PROJECTION_LIST_ITEM_ID_ATTRIBUTE);
          const isContinuation = itemId && seenItemIds.has(itemId);
          if (isContinuation) {
            item.classList.add('list-item-continuation');
            item.setAttribute('data-list-continuation', 'true');
            if (ordered) {
              const logicalValue = logicalValuesByItemId.get(itemId);
              if (Number.isFinite(logicalValue)) item.setAttribute('value', String(logicalValue));
            }
            return;
          }

          if (itemId) seenItemIds.add(itemId);
          if (!ordered) return;
          const explicitValue = Number(item.getAttribute('value'));
          const logicalValue = Number.isFinite(explicitValue) && item.hasAttribute('value')
            ? explicitValue
            : nextValue;
          if (itemId) logicalValuesByItemId.set(itemId, logicalValue);
          nextValue = logicalValue + 1;
        });
    });
  });

  containers.forEach((container) => {
    container.querySelectorAll(`[${PROJECTION_LIST_ID_ATTRIBUTE}]`).forEach((list) => {
      list.removeAttribute(PROJECTION_LIST_ID_ATTRIBUTE);
      if (!Array.from(list.children).some((child) => child.tagName === 'LI')) list.remove();
    });
    container.querySelectorAll(`[${PROJECTION_LIST_ITEM_ID_ATTRIBUTE}]`).forEach((item) => {
      item.removeAttribute(PROJECTION_LIST_ITEM_ID_ATTRIBUTE);
    });
  });
  return containers.map((container) => container.innerHTML);
};

const markProjectedTextBlockContinuations = (pages) => {
  const containers = pages.map((html) => {
    const container = document.createElement('div');
    container.innerHTML = html || '';
    return container;
  });
  const occurrencesByBlockId = new Map();

  containers.forEach((container) => {
    container.querySelectorAll(`[${PROJECTION_TEXT_BLOCK_ID_ATTRIBUTE}]`).forEach((block) => {
      const blockId = block.getAttribute(PROJECTION_TEXT_BLOCK_ID_ATTRIBUTE);
      if (!blockId) return;
      const occurrences = occurrencesByBlockId.get(blockId) || [];
      occurrences.push(block);
      occurrencesByBlockId.set(blockId, occurrences);
    });
  });

  occurrencesByBlockId.forEach((occurrences, blockId) => {
    if (occurrences.length < 2) return;
    occurrences.forEach((block, index) => {
      block.setAttribute(SAVED_TEXT_BLOCK_ID_ATTRIBUTE, blockId);
      if (index > 0) block.setAttribute(SAVED_TEXT_BLOCK_CONTINUATION_ATTRIBUTE, 'true');
    });
  });

  containers.forEach((container) => {
    container.querySelectorAll(`[${PROJECTION_TEXT_BLOCK_ID_ATTRIBUTE}]`).forEach((block) => {
      block.removeAttribute(PROJECTION_TEXT_BLOCK_ID_ATTRIBUTE);
    });
  });
  return containers.map((container) => container.innerHTML);
};

const mergeSavedTextBlockContinuations = (bodyPages) => {
  const container = document.createElement('div');
  removePageBoundaryPlaceholderParagraphs(bodyPages)
    .forEach((html) => container.insertAdjacentHTML('beforeend', html || ''));
  const startsByBlockId = new Map();

  container.querySelectorAll(`[${SAVED_TEXT_BLOCK_ID_ATTRIBUTE}]`).forEach((block) => {
    const blockId = block.getAttribute(SAVED_TEXT_BLOCK_ID_ATTRIBUTE);
    const isContinuation = block.getAttribute(SAVED_TEXT_BLOCK_CONTINUATION_ATTRIBUTE) === 'true';
    const start = startsByBlockId.get(blockId);
    if (!isContinuation) {
      if (blockId && !start) startsByBlockId.set(blockId, block);
      return;
    }
    if (!start || start.tagName !== block.tagName) return;
    while (block.firstChild) start.appendChild(block.firstChild);
    block.remove();
  });

  container.querySelectorAll(
    `[${SAVED_TEXT_BLOCK_ID_ATTRIBUTE}], [${SAVED_TEXT_BLOCK_CONTINUATION_ATTRIBUTE}]`
  ).forEach((block) => {
    block.removeAttribute(SAVED_TEXT_BLOCK_ID_ATTRIBUTE);
    block.removeAttribute(SAVED_TEXT_BLOCK_CONTINUATION_ATTRIBUTE);
  });
  return container.innerHTML || '<p><br></p>';
};

const getLiveBodyPagesFromEditor = (editor) => {
  const root = editor?.view?.dom;
  if (!(root instanceof HTMLElement)) return null;
  const projectionRoot = root.cloneNode(true);
  projectionRoot.querySelectorAll(PROJECTED_TEXT_BLOCK_SELECTOR).forEach((block, index) => {
    block.setAttribute(PROJECTION_TEXT_BLOCK_ID_ATTRIBUTE, `text-block-${index}`);
  });
  projectionRoot.querySelectorAll('ol, ul').forEach((list, index) => {
    list.setAttribute(PROJECTION_LIST_ID_ATTRIBUTE, `list-${index}`);
  });
  projectionRoot.querySelectorAll('li').forEach((item, index) => {
    item.setAttribute(PROJECTION_LIST_ITEM_ID_ATTRIBUTE, `list-item-${index}`);
  });
  const spacers = Array.from(projectionRoot.querySelectorAll('.v2-page-break-spacer'));
  if (!spacers.length) return null;
  const pages = [];
  let startAfter = null;
  spacers.forEach((spacer) => {
    const range = document.createRange();
    if (startAfter) range.setStartAfter(startAfter);
    else range.setStart(projectionRoot, 0);
    range.setEndBefore(spacer);
    pages.push(serializeDomFragment(range.cloneContents()));
    startAfter = spacer;
  });
  const tailRange = document.createRange();
  tailRange.setStartAfter(startAfter);
  tailRange.setEnd(projectionRoot, projectionRoot.childNodes.length);
  pages.push(serializeDomFragment(tailRange.cloneContents()));
  return pages.length > 1
    ? markProjectedTextBlockContinuations(normalizeProjectedListPages(pages))
    : null;
};

const fitsWithHost = (host, parts, candidate = '') => {
  host.innerHTML = `${parts.join('')}${candidate}`;
  return host.scrollHeight <= host.clientHeight + 1;
};

const trimCloneByCharRange = (sourceEl, fromChar, toChar) => {
  const clone = sourceEl.cloneNode(true);
  const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  let cursor = 0;
  nodes.forEach((tn) => {
    const text = tn.textContent || '';
    const start = cursor;
    const end = cursor + text.length;
    cursor = end;
    if (end <= fromChar || start >= toChar) {
      tn.textContent = '';
      return;
    }
    const keepStart = Math.max(0, fromChar - start);
    const keepEnd = Math.min(text.length, toChar - start);
    tn.textContent = text.slice(keepStart, keepEnd);
  });
  return clone;
};

const splitTextNode = (textNode, host, currentParts) => {
  const text = textNode.textContent || '';
  if (text.length < 2) return null;
  let lo = 1;
  let hi = text.length - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (fitsWithHost(host, currentParts, escapeHtml(text.slice(0, mid)))) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (best <= 0 || best >= text.length) return null;
  return {
    fitHtml: escapeHtml(text.slice(0, best)),
    overflowHtml: escapeHtml(text.slice(best)),
  };
};

const splitElementTextOnly = (elementNode, host, currentParts) => {
  if (!SAFE_TEXT_SPLIT_TAGS.has(elementNode.tagName)) return null;
  const textLength = (elementNode.textContent || '').length;
  if (textLength < 2) return null;
  let lo = 1;
  let hi = textLength - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const fit = trimCloneByCharRange(elementNode, 0, mid).outerHTML;
    if (fitsWithHost(host, currentParts, fit)) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (best <= 0 || best >= textLength) return null;
  const fit = trimCloneByCharRange(elementNode, 0, best);
  const overflow = trimCloneByCharRange(elementNode, best, textLength);
  const blockId = elementNode.getAttribute(SAVED_TEXT_BLOCK_ID_ATTRIBUTE) ||
    `fallback-text-block-${++fallbackTextBlockId}`;
  fit.setAttribute(SAVED_TEXT_BLOCK_ID_ATTRIBUTE, blockId);
  overflow.setAttribute(SAVED_TEXT_BLOCK_ID_ATTRIBUTE, blockId);
  if (elementNode.getAttribute(SAVED_TEXT_BLOCK_CONTINUATION_ATTRIBUTE) === 'true') {
    fit.setAttribute(SAVED_TEXT_BLOCK_CONTINUATION_ATTRIBUTE, 'true');
  }
  overflow.setAttribute(SAVED_TEXT_BLOCK_CONTINUATION_ATTRIBUTE, 'true');
  return {
    fitHtml: fit.outerHTML,
    overflowHtml: overflow.outerHTML,
  };
};

const buildListFragment = (sourceList, items, startOffset = 0) => {
  const list = sourceList.cloneNode(false);
  if (list.tagName === 'OL') {
    const originalStart = Number(sourceList.getAttribute('start')) || 1;
    list.setAttribute('start', String(originalStart + startOffset));
  }
  items.forEach((item) => list.appendChild(item.cloneNode(true)));
  return list.outerHTML;
};

const splitListForPage = (listNode, host, currentParts) => {
  const items = Array.from(listNode.children).filter((child) => child.tagName === 'LI');
  if (!items.length) return null;
  let fitCount = 0;
  for (let count = 1; count <= items.length; count += 1) {
    const candidate = buildListFragment(listNode, items.slice(0, count));
    if (!fitsWithHost(host, currentParts, candidate)) break;
    fitCount = count;
  }
  if (fitCount > 0 && fitCount < items.length) {
    return {
      fitHtml: buildListFragment(listNode, items.slice(0, fitCount)),
      overflowHtml: buildListFragment(listNode, items.slice(fitCount), fitCount),
    };
  }
  if (fitCount === items.length) return null;

  const firstItem = items[0];
  const textLength = (firstItem.textContent || '').length;
  if (textLength < 2) return null;
  let low = 1;
  let high = textLength - 1;
  let best = -1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const firstPart = trimCloneByCharRange(firstItem, 0, middle);
    if (fitsWithHost(host, currentParts, buildListFragment(listNode, [firstPart]))) {
      best = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  if (best <= 0 || best >= textLength) return null;
  const fitItem = trimCloneByCharRange(firstItem, 0, best);
  const overflowItem = trimCloneByCharRange(firstItem, best, textLength);
  overflowItem.classList.add('list-item-continuation');
  overflowItem.setAttribute('data-list-continuation', 'true');
  if (listNode.tagName === 'OL') {
    const logicalValue = Number(firstItem.getAttribute('value')) || Number(listNode.getAttribute('start')) || 1;
    overflowItem.setAttribute('value', String(logicalValue));
  }
  return {
    fitHtml: buildListFragment(listNode, [fitItem]),
    overflowHtml: buildListFragment(listNode, [overflowItem, ...items.slice(1)]),
  };
};

const paginateBody = (bodyHtml, regionSpaces) => {
  const queue = parseHtmlNodes(bodyHtml || '');
  if (!queue.length) return ['<p><br></p>'];
  const host = createMeasureHost(regionSpaces);
  try {
    const pages = [];
    let current = [];
    let guard = 0;
    setMeasureHostPageRegionSpaces(host, regionSpaces, 0);
    while (queue.length && guard < 20000) {
      guard += 1;
      const node = queue.shift();
      const html = serializeNode(node);
      if (!html) continue;
      if (fitsWithHost(host, current, html)) {
        current.push(html);
        continue;
      }
      const split = node.nodeType === Node.ELEMENT_NODE && (node.tagName === 'OL' || node.tagName === 'UL')
        ? splitListForPage(node, host, current)
        :
        node.nodeType === Node.TEXT_NODE
          ? splitTextNode(node, host, current)
          : node.nodeType === Node.ELEMENT_NODE
            ? splitElementTextOnly(node, host, current)
            : null;
      if (split && split.fitHtml && split.overflowHtml && split.fitHtml.trim() && split.overflowHtml.trim()) {
        current.push(split.fitHtml);
        pages.push(current.join(''));
        current = [];
        setMeasureHostPageRegionSpaces(host, regionSpaces, pages.length);
        const overflowNodes = parseHtmlNodes(split.overflowHtml);
        queue.unshift(...overflowNodes);
        continue;
      }
      if (current.length > 0) {
        pages.push(current.join(''));
        current = [];
        setMeasureHostPageRegionSpaces(host, regionSpaces, pages.length);
        queue.unshift(node);
        continue;
      }
      pages.push(html);
    }
    if (current.length > 0) pages.push(current.join(''));
    return pages.length ? pages : ['<p><br></p>'];
  } finally {
    document.body.removeChild(host);
  }
};

const DocumentEditorV2 = ({
  documentId,
  className,
  debugInitialDocument = null,
  embedded = false,
  engineRef = null,
  onDirty = null,
}) => {
  const { user, isAdmin } = useAuthStore();
  const isUserAdmin = isAdmin();
  const {
    currentDocument,
    setCurrentDocument,
    updateDocument,
    createDocument,
    loadDocument: storeLoadDocument,
    uploadAudioResource,
    removeAudioResource,
  } = useDocumentStore();

  const [documentName, setDocumentName] = useState('Untitled Document');
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [surfacePageCount, setSurfacePageCount] = useState(1);
  const [hoveredPageIndex, setHoveredPageIndex] = useState(null);
  const [mathEditor, setMathEditor] = useState(null);
  const [slashMenu, setSlashMenu] = useState({
    open: false,
    x: 0,
    y: 0,
    position: 0,
    selectedIndex: 0,
  });
  const [selectedResponseAreaId, setSelectedResponseAreaId] = useState(null);
  const [stepImageTarget, setStepImageTarget] = useState(null);
  const [stepAudioTarget, setStepAudioTarget] = useState(null);
  const [isUploadingStepAudio, setIsUploadingStepAudio] = useState(false);
  const [stepAudioError, setStepAudioError] = useState('');
  const [stepEmbedTarget, setStepEmbedTarget] = useState(null);
  const [stepEmbedMode, setStepEmbedMode] = useState('html');
  const [stepEmbedHtml, setStepEmbedHtml] = useState('');
  const [stepEmbedUrl, setStepEmbedUrl] = useState('');
  const [stepEmbedError, setStepEmbedError] = useState('');
  const [selectedHeaderImage, setSelectedHeaderImage] = useState(null);
  const [headerImageRect, setHeaderImageRect] = useState(null);
  const editablePagesRef = useRef(null);
  const isApplyingStateRef = useRef(false);
  const editorInstanceRef = useRef(null);
  const loadedDocumentRef = useRef(null);
  const openMathEditorRef = useRef(() => {});
  const deletePageRef = useRef(() => false);
  const pendingRegionEditsRef = useRef({});
  const slashMenuRef = useRef(slashMenu);
  const openSlashMenuRef = useRef(() => false);
  const closeSlashMenuRef = useRef(() => {});
  const moveSlashMenuSelectionRef = useRef(() => {});
  const executeSelectedSlashCommandRef = useRef(() => {});
  const openResponseAreaRef = useRef(() => {});
  const deleteResponseAreaRef = useRef(() => {});
  const openStepImageRef = useRef(() => {});
  const openStepAudioRef = useRef(() => {});
  const openStepEmbedRef = useRef(() => {});
  const replaceStepAudioRef = useRef(() => {});
  const removeStepAudioRef = useRef(() => {});
  const editStepEmbedRef = useRef(() => {});

  openResponseAreaRef.current = (responseId) => {
    if (responseId) setSelectedResponseAreaId(responseId);
  };
  deleteResponseAreaRef.current = (responseId, position, nodeSize) => {
    const instance = editorInstanceRef.current;
    if (instance && Number.isFinite(position)) {
      instance.commands.deleteRange({ from: position, to: position + nodeSize });
    }
    const doc = useDocumentStore.getState().currentDocument;
    if (!doc || !responseId) return;
    const assessment = doc.assessment || {};
    const responseAreas = { ...(assessment.responseAreas || {}) };
    delete responseAreas[responseId];
    useDocumentStore.getState().setCurrentDocument({
      ...doc,
      assessment: {
        ...assessment,
        enabled: assessment.enabled === true,
        responseAreas,
        responseAreaOrder: (assessment.responseAreaOrder || []).filter((id) => id !== responseId),
      },
    });
  };
  openStepImageRef.current = (position) => setStepImageTarget({ position });
  openStepAudioRef.current = (stepPosition) => {
    const stepNode = editorInstanceRef.current?.state.doc.nodeAt(stepPosition);
    let existing = null;
    stepNode?.descendants((child, offset) => {
      if (!existing && child.type.name === 'documentStepAudio') {
        existing = {
          stepPosition,
          nodePosition: stepPosition + 1 + offset,
          existingPath: child.attrs.path || '',
        };
      }
    });
    setStepAudioError('');
    setStepAudioTarget(existing || { stepPosition, nodePosition: null, existingPath: '' });
  };
  replaceStepAudioRef.current = (nodePosition, attrs) => {
    setStepAudioError('');
    setStepAudioTarget({
      stepPosition: null,
      nodePosition,
      existingPath: attrs.path || '',
    });
  };
  removeStepAudioRef.current = (position, nodeSize, path) => {
    const instance = editorInstanceRef.current;
    if (instance && Number.isFinite(position)) {
      instance.commands.deleteRange({ from: position, to: position + nodeSize });
    }
    removeAudioResource(path).catch(() => {});
  };
  openStepEmbedRef.current = (kind, stepPosition, position) => {
    const instance = editorInstanceRef.current;
    let hasEmbed = false;
    instance?.state.doc.nodeAt(stepPosition)?.descendants((child) => {
      if (child.type.name === 'documentStepEmbed') hasEmbed = true;
    });
    if (hasEmbed) return;
    setStepEmbedMode(kind);
    setStepEmbedHtml('');
    setStepEmbedUrl('');
    setStepEmbedError('');
    setStepEmbedTarget({ stepPosition, position, nodePosition: null });
  };
  editStepEmbedRef.current = (nodePosition, attrs) => {
    setStepEmbedMode(attrs.kind || 'html');
    setStepEmbedHtml(attrs.html || '');
    setStepEmbedUrl(attrs.src || '');
    setStepEmbedError('');
    setStepEmbedTarget({ nodePosition, position: nodePosition, stepPosition: null });
  };

  slashMenuRef.current = slashMenu;

  const closeSlashMenu = useCallback(() => {
    setSlashMenu((current) => {
      if (!current.open) return current;
      const next = { ...current, open: false };
      slashMenuRef.current = next;
      return next;
    });
  }, []);

  closeSlashMenuRef.current = closeSlashMenu;
  openSlashMenuRef.current = (view) => {
    if (!view.state.selection.empty) return false;
    const position = view.state.selection.from;
    const coords = view.coordsAtPos(position);
    const padding = 8;
    const x = Math.max(
      padding,
      Math.min(coords.left, window.innerWidth - SLASH_COMMAND_MENU_WIDTH_PX - padding)
    );
    const roomBelow = window.innerHeight - coords.bottom;
    const y = roomBelow >= SLASH_COMMAND_MENU_HEIGHT_PX
      ? coords.bottom + 6
      : Math.max(padding, coords.top - SLASH_COMMAND_MENU_HEIGHT_PX - 6);
    const next = { open: true, x, y, position, selectedIndex: 0 };
    slashMenuRef.current = next;
    setSlashMenu(next);
    return true;
  };
  moveSlashMenuSelectionRef.current = (direction) => {
    setSlashMenu((current) => {
      if (!current.open) return current;
      const selectedIndex = (
        current.selectedIndex + direction + SLASH_COMMANDS.length
      ) % SLASH_COMMANDS.length;
      const next = { ...current, selectedIndex };
      slashMenuRef.current = next;
      return next;
    });
  };

  const pages = useMemo(() => {
    const p = currentDocument?.pages;
    return Array.isArray(p) && p.length > 0 ? p : [''];
  }, [currentDocument?.pages]);

  const regions = useMemo(() => getCommonRegions(pages), [pages]);
  const headerRepeatsOnAllPages = useMemo(() => {
    return doesHeaderRepeat(regions.headerHtml);
  }, [regions.headerHtml]);
  const regionSpaces = useMemo(
    () => {
      const headerSpacePx = measureRegionSpacePx(regions.headerHtml);
      return {
        topSpacePx: headerSpacePx,
        subsequentTopSpacePx: headerRepeatsOnAllPages ? headerSpacePx : REGION_MIN_SPACE_PX,
        bottomSpacePx: measureRegionSpacePx(regions.footerHtml),
      };
    },
    [headerRepeatsOnAllPages, regions.footerHtml, regions.headerHtml]
  );

  useEffect(() => {
    setSurfacePageCount((current) => Math.max(current, 1, pages.length));
  }, [pages.length]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    return () => cancelAnimationFrame(frame);
  }, [regionSpaces.bottomSpacePx, regionSpaces.subsequentTopSpacePx, regionSpaces.topSpacePx, surfacePageCount]);

  useEffect(() => {
    const canvas = editablePagesRef.current;
    if (!canvas) return undefined;
    const handlePageCount = (event) => {
      const requested = Number(event.detail) || 1;
      setSurfacePageCount(Math.max(1, requested));
    };
    canvas.addEventListener('v2surfacepagecount', handlePageCount);
    return () => canvas.removeEventListener('v2surfacepagecount', handlePageCount);
  }, [pages.length]);

  const getCombinedBodyHtml = useCallback((docPages) => {
    const src = Array.isArray(docPages) && docPages.length > 0 ? docPages : [''];
    const bodyPages = src.map((page) => getPageRegions(page || '').bodyHtml);
    return mergeSavedTextBlockContinuations(bodyPages);
  }, []);

  const updateLocalPages = useCallback((nextPages) => {
    const doc = useDocumentStore.getState().currentDocument;
    if (!doc) return;
    const currentPages = Array.isArray(doc.pages) ? doc.pages : [''];
    if (
      currentPages.length === nextPages.length &&
      currentPages.every((page, index) => page === nextPages[index])
    ) return;
    useDocumentStore.getState().setCurrentDocument({ ...doc, pages: nextPages });
  }, []);

  useEffect(() => {
    openMathEditorRef.current = ({ type, pos, to = pos, latex = '', isNew = false }) => {
      const instance = editorInstanceRef.current;
      const nodeDom = Number.isFinite(pos) ? instance?.view?.nodeDOM(pos) : null;
      const selectionRect = instance?.view?.coordsAtPos(instance.state.selection.from);
      const rect = nodeDom instanceof HTMLElement ? nodeDom.getBoundingClientRect() : selectionRect;
      setMathEditor({
        type,
        pos,
        to,
        latex,
        isNew,
        anchorRect: rect ? { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom } : null,
      });
    };
  }, []);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      TableKit.configure({
        table: {
          resizable: true,
          handleWidth: 6,
          cellMinWidth: 50,
          lastColumnResizable: true,
        },
      }),
      Underline,
      TextStyleKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextIndent,
      ListMarkerStyle,
      ResizableImage,
      SectionDivider,
      ResponseAreaBlock.configure({
        onOpen: (responseId) => openResponseAreaRef.current(responseId),
        onDelete: (responseId, position, nodeSize) =>
          deleteResponseAreaRef.current(responseId, position, nodeSize),
      }),
      DocumentStepAudio.configure({
        onReplace: (position, attrs) => replaceStepAudioRef.current(position, attrs),
        onRemove: (position, nodeSize, path) =>
          removeStepAudioRef.current(position, nodeSize, path),
      }),
      DocumentStepEmbed.configure({
        onEdit: (position, attrs) => editStepEmbedRef.current(position, attrs),
      }),
      DocumentStepBlock.configure({
        onOpenMath: (position) => {
          openMathEditorRef.current({
            type: 'inline',
            pos: position,
            isNew: true,
          });
        },
        onOpenImage: (position) => openStepImageRef.current(position),
        onOpenAudio: (stepPosition) => openStepAudioRef.current(stepPosition),
        onOpenEmbed: (kind, stepPosition, position) =>
          openStepEmbedRef.current(kind, stepPosition, position),
      }),
      EditablePagination,
      Mathematics.configure({
        katexOptions: KATEX_OPTIONS,
        inlineOptions: {
          onClick: (node, pos) => {
            openMathEditorRef.current({ type: 'inline', pos, latex: node.attrs.latex || '', isNew: false });
          },
        },
        blockOptions: {
          onClick: (node, pos) => {
            openMathEditorRef.current({ type: 'block', pos, latex: node.attrs.latex || '', isNew: false });
          },
        },
      }),
    ],
    content: '<p><br></p>',
    editorProps: {
      attributes: {
        class: 'v2-model-editor-content',
        spellcheck: 'true',
      },
      handleTextInput: (view, from, to, text) => {
        if (text !== '$') return false;
        const $from = view.state.doc.resolve(from);
        if (!$from.parent.isTextblock || $from.parentOffset !== $from.parent.content.size) return false;

        const candidate = `${$from.parent.textBetween(0, $from.parent.content.size, '', '')}$`;
        const match = candidate.match(/^\$\$([^$\n]+)\$\$$/);
        const latex = match?.[1]?.trim();
        const blockMath = view.state.schema.nodes.blockMath;
        if (!latex || !blockMath) return false;

        const parentIndex = $from.index(-1);
        const canReplaceTextblock =
          $from.depth > 0 &&
          $from.node(-1).canReplaceWith(parentIndex, parentIndex + 1, blockMath);
        const tr = canReplaceTextblock
          ? view.state.tr.replaceWith($from.before(), $from.after(), blockMath.create({ latex }))
          : view.state.tr.replaceWith($from.start(), to, view.state.schema.nodes.inlineMath.create({ latex }));
        view.dispatch(tr.scrollIntoView());
        return true;
      },
      handlePaste: (view, event) => {
        const clipboard = event.clipboardData;
        const text = clipboard?.getData('text/plain') || '';
        const hasClipboardText = text.trim().length > 0;
        const items = clipboard?.items ? Array.from(clipboard.items) : [];
        const imageItem = items.find((item) => item.type?.startsWith('image/'));
        const imageFile = imageItem?.getAsFile?.() ||
          (clipboard?.files ? Array.from(clipboard.files).find((file) => file.type?.startsWith('image/')) : null);
        const html = clipboard?.getData('text/html') || '';
        const htmlHost = document.createElement('div');
        htmlHost.innerHTML = html;
        const pastedImage = htmlHost.querySelector('img[src]');
        if (!imageFile && htmlHost.querySelector('table')) {
          event.preventDefault();
          const normalizedHtml = normalizeMathForModel(normalizeLoadedBodyHtml(html));
          editorInstanceRef.current?.commands.insertContent(normalizedHtml);
          window.setTimeout(() => {
            const instance = editorInstanceRef.current;
            if (instance) migrateInlineMathStrings(instance);
          }, 0);
          return true;
        }
        // Rich-text clipboards frequently include a tracking, equation, or
        // decorative image next to otherwise normal text. Treat the paste as
        // an image only when it contains an actual image file or no usable
        // text; otherwise this branch cancels the paste and loses all text.
        if (imageFile || (pastedImage && !hasClipboardText)) {
          event.preventDefault();
          const { from, to } = view.state.selection;
          const insertImage = async () => {
            const imageData = imageFile
              ? await readClipboardImage(imageFile, user?.uid)
              : {
                  src: await persistImageSource(
                    pastedImage.getAttribute('src'),
                    user?.uid,
                    pastedImage.getAttribute('alt') || 'pasted-image.png'
                  ),
                };
            if (!imageData.naturalWidth && imageData.src) {
              Object.assign(imageData, await getImageDimensions(imageData.src));
            }
            const instance = editorInstanceRef.current;
            if (!instance || !imageData.src) return;
            instance.chain().focus().insertContentAt({ from, to }, {
              type: 'resizableImage',
              attrs: {
                ...imageData,
                alt: pastedImage?.getAttribute('alt') || imageFile?.name || '',
                width: 300,
                align: 'left',
              },
            }).run();
          };
          insertImage().catch((error) => console.error('V2 image paste failed:', error));
          return true;
        }
        if (!text) return false;
        const instance = editorInstanceRef.current;
        if (!instance || instance.isDestroyed) return false;
        const { from, to } = view.state.selection;
        const inserted = instance.commands.insertContentAt(
          { from, to },
          normalizePlainTextToHtml(text),
          { updateSelection: true }
        );
        if (!inserted) return false;
        event.preventDefault();
        window.setTimeout(() => {
          const currentInstance = editorInstanceRef.current;
          if (currentInstance) migrateInlineMathStrings(currentInstance);
        }, 0);
        return true;
      },
      handleKeyDown: (view, event) => {
        if (slashMenuRef.current.open) {
          if (event.key === 'Escape') {
            event.preventDefault();
            closeSlashMenuRef.current();
            return true;
          }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            moveSlashMenuSelectionRef.current(event.key === 'ArrowDown' ? 1 : -1);
            return true;
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            executeSelectedSlashCommandRef.current();
            return true;
          }
          if (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete') {
            closeSlashMenuRef.current();
          }
        }

        if (
          (event.key === 'Backspace' || event.key === 'Delete') &&
          !view.state.selection.empty
        ) {
          event.preventDefault();
          view.dispatch(view.state.tr.deleteSelection().scrollIntoView());
          return true;
        }

        if (
          event.key === '/' &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey &&
          !event.shiftKey
        ) {
          event.preventDefault();
          return openSlashMenuRef.current(view);
        }

        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'm') {
          event.preventDefault();
          const { from, to } = view.state.selection;
          const selectedText = view.state.doc.textBetween(from, to, ' ').trim();
          const latex = selectedText.startsWith('$') && selectedText.endsWith('$')
            ? selectedText.slice(1, -1)
            : selectedText;
          openMathEditorRef.current({
            type: 'inline',
            pos: from,
            to,
            latex,
            isNew: true,
          });
          return true;
        }

        if (
          event.key !== 'Delete' ||
          !view.state.selection.empty ||
          view.state.selection.to !== view.state.doc.content.size
        ) return false;

        const modelHtml = editorInstanceRef.current?.getHTML() || '';
        const bodyPages = paginateBody(materializeMathForProjection(modelHtml), regionSpaces);
        const lastPageIndex = bodyPages.length - 1;
        if (lastPageIndex < 1 || hasMeaningfulHtml(bodyPages[lastPageIndex])) return false;

        event.preventDefault();
        return deletePageRef.current(lastPageIndex);
      },
    },
    onCreate: ({ editor: instance }) => {
      editorInstanceRef.current = instance;
    },
    onUpdate: () => {
      if (!isApplyingStateRef.current) {
        onDirty?.();
      }
    },
  });

  useEffect(() => {
    editorInstanceRef.current = editor;
    return () => {
      if (editorInstanceRef.current === editor) editorInstanceRef.current = null;
    };
  }, [editor]);

  const executeSlashCommand = useCallback((commandId) => {
    const instance = editorInstanceRef.current;
    const menu = slashMenuRef.current;
    if (!instance || !menu.open) return false;

    closeSlashMenuRef.current();
    const chain = instance.chain().focus().setTextSelection(menu.position);
    if (commandId === 'step') {
      return chain.insertContent([
        {
          type: 'documentStep',
          attrs: {
            stepId: `step_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            stepNumber: '',
            title: '',
          },
          content: [{ type: 'paragraph' }],
        },
        { type: 'paragraph' },
      ]).run();
    }

    if (!RESPONSE_AREA_TYPE_LABELS[commandId]) return false;
    const responseId = `ra_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const config = {
      type: commandId,
      label: '',
      required: true,
      options: (commandId === 'multiple-choice' || commandId === 'multi-select') ? ['', ''] : null,
      points: 1,
      correctAnswer: null,
      correctAnswers: null,
    };
    const inserted = chain.insertContent([
      {
        type: 'responseArea',
        attrs: {
          responseId,
          responseType: commandId,
          configJson: JSON.stringify(config),
        },
      },
      { type: 'paragraph' },
    ]).run();
    if (!inserted) return false;

    const doc = useDocumentStore.getState().currentDocument;
    if (doc) {
      const assessment = doc.assessment || {};
      useDocumentStore.getState().setCurrentDocument({
        ...doc,
        assessment: {
          ...assessment,
          enabled: assessment.enabled === true,
          responseAreas: {
            ...(assessment.responseAreas || {}),
            [responseId]: config,
          },
          responseAreaOrder: [...(assessment.responseAreaOrder || []), responseId],
        },
      });
    }
    return true;
  }, []);

  executeSelectedSlashCommandRef.current = () => {
    const menu = slashMenuRef.current;
    const command = SLASH_COMMANDS[menu.selectedIndex];
    if (command) executeSlashCommand(command.id);
  };

  useEffect(() => {
    if (!slashMenu.open) return undefined;
    const closeOnPointerDown = (event) => {
      if (event.target.closest?.('[data-v2-slash-menu="true"]')) return;
      closeSlashMenu();
    };
    const closeOnViewportChange = () => closeSlashMenu();
    document.addEventListener('mousedown', closeOnPointerDown);
    window.addEventListener('resize', closeOnViewportChange);
    window.addEventListener('scroll', closeOnViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', closeOnPointerDown);
      window.removeEventListener('resize', closeOnViewportChange);
      window.removeEventListener('scroll', closeOnViewportChange, true);
    };
  }, [closeSlashMenu, slashMenu.open]);

  useEffect(() => {
    if (!editor) return;
    const responseAreas = currentDocument?.assessment?.responseAreas || {};
    let transaction = editor.state.tr;
    let changed = false;
    editor.state.doc.descendants((node, position) => {
      if (node.type.name !== 'responseArea' || !node.attrs.responseId) return;
      const config = responseAreas[node.attrs.responseId];
      if (!config) return;
      const configJson = JSON.stringify(config);
      if (
        node.attrs.configJson === configJson &&
        node.attrs.responseType === (config.type || node.attrs.responseType)
      ) return;
      transaction = transaction.setNodeMarkup(position, undefined, {
        ...node.attrs,
        responseType: config.type || node.attrs.responseType,
        configJson,
      });
      changed = true;
    });
    if (changed) editor.view.dispatch(transaction);
  }, [currentDocument?.assessment?.responseAreas, editor]);

  const duplicateSelectedResponseArea = useCallback(() => {
    const instance = editorInstanceRef.current;
    const sourceId = selectedResponseAreaId;
    const doc = useDocumentStore.getState().currentDocument;
    const sourceConfig = doc?.assessment?.responseAreas?.[sourceId];
    if (!instance || !sourceId || !sourceConfig) return false;

    let sourceNode = null;
    let sourcePosition = null;
    instance.state.doc.descendants((node, position) => {
      if (sourceNode || node.type.name !== 'responseArea' || node.attrs.responseId !== sourceId) return;
      sourceNode = node;
      sourcePosition = position;
    });
    if (!sourceNode || !Number.isFinite(sourcePosition)) return false;

    const responseId = `ra_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const config = JSON.parse(JSON.stringify(sourceConfig));
    const inserted = instance.commands.insertContentAt(sourcePosition + sourceNode.nodeSize, {
      type: 'responseArea',
      attrs: {
        responseId,
        responseType: config.type || sourceNode.attrs.responseType,
        configJson: JSON.stringify(config),
      },
    });
    if (!inserted) return false;

    const assessment = doc.assessment || {};
    const responseAreaOrder = [...(assessment.responseAreaOrder || [])];
    const sourceIndex = responseAreaOrder.indexOf(sourceId);
    responseAreaOrder.splice(sourceIndex >= 0 ? sourceIndex + 1 : responseAreaOrder.length, 0, responseId);
    useDocumentStore.getState().setCurrentDocument({
      ...doc,
      assessment: {
        ...assessment,
        enabled: assessment.enabled === true,
        responseAreas: {
          ...(assessment.responseAreas || {}),
          [responseId]: config,
        },
        responseAreaOrder,
      },
    });
    setSelectedResponseAreaId(null);
    return true;
  }, [selectedResponseAreaId]);

  const handleStepImageInsert = useCallback(async ({ url, align }) => {
    const instance = editorInstanceRef.current;
    const position = stepImageTarget?.position;
    if (!instance || !url || !Number.isFinite(position)) return false;
    const persistedSrc = await persistImageSource(url, user?.uid, 'step-image.png');
    const dimensions = await getImageDimensions(persistedSrc);
    const inserted = instance.commands.insertContentAt(position, {
      type: 'resizableImage',
      attrs: {
        src: persistedSrc,
        alt: '',
        align: align || 'left',
        width: 300,
        ...dimensions,
      },
    });
    setStepImageTarget(null);
    return inserted;
  }, [stepImageTarget?.position, user?.uid]);

  const handleStepAudioSave = useCallback(async ({ file, duration }) => {
    const instance = editorInstanceRef.current;
    if (!instance || !file || !user?.uid || !stepAudioTarget) return false;
    try {
      setIsUploadingStepAudio(true);
      setStepAudioError('');
      const audio = await uploadAudioResource(
        file,
        user.uid,
        stepAudioTarget.existingPath || null,
        { duration }
      );
      const attrs = {
        url: audio.url || '',
        name: audio.name || file.name || 'Step audio',
        contentType: audio.contentType || file.type || 'audio/webm',
        path: audio.path || '',
        duration: Number(audio.duration ?? duration) || 0,
      };
      let inserted = false;
      if (Number.isFinite(stepAudioTarget.nodePosition)) {
        const current = instance.state.doc.nodeAt(stepAudioTarget.nodePosition);
        if (current?.type.name === 'documentStepAudio') {
          inserted = instance.commands.command(({ tr }) => {
            tr.setNodeMarkup(stepAudioTarget.nodePosition, undefined, attrs);
            return true;
          });
        }
      } else if (Number.isFinite(stepAudioTarget.stepPosition)) {
        inserted = instance.commands.insertContentAt(stepAudioTarget.stepPosition + 1, {
          type: 'documentStepAudio',
          attrs,
        });
      }
      if (inserted) {
        setStepAudioTarget(null);
        return true;
      }
      setStepAudioError('Could not insert the step audio.');
      return false;
    } catch (error) {
      setStepAudioError(error.message || 'Failed to save step audio.');
      return false;
    } finally {
      setIsUploadingStepAudio(false);
    }
  }, [stepAudioTarget, uploadAudioResource, user?.uid]);

  const closeStepEmbedModal = useCallback(() => {
    setStepEmbedTarget(null);
    setStepEmbedError('');
    setStepEmbedHtml('');
    setStepEmbedUrl('');
  }, []);

  const handleStepEmbedSave = useCallback(() => {
    const instance = editorInstanceRef.current;
    if (!instance || !stepEmbedTarget) return false;
    const attrs = { kind: stepEmbedMode, src: '', html: '' };
    if (stepEmbedMode === 'html') {
      attrs.html = sanitizeStepEmbedHtml(stepEmbedHtml);
      if (!attrs.html) {
        setStepEmbedError('Paste valid HTML to embed.');
        return false;
      }
    } else if (stepEmbedMode === 'url') {
      attrs.src = getSafeStepEmbedUrl(stepEmbedUrl);
      if (!attrs.src) {
        setStepEmbedError('Enter a valid http or https URL.');
        return false;
      }
    } else {
      if (!getStepYouTubeEmbedUrl(stepEmbedUrl)) {
        setStepEmbedError('Enter a valid YouTube URL or 11-character video ID.');
        return false;
      }
      attrs.src = stepEmbedUrl.trim();
    }

    let inserted = false;
    if (Number.isFinite(stepEmbedTarget.nodePosition)) {
      inserted = instance.commands.command(({ tr }) => {
        tr.setNodeMarkup(stepEmbedTarget.nodePosition, undefined, attrs);
        return true;
      });
    } else {
      inserted = instance.commands.insertContentAt(stepEmbedTarget.position, {
        type: 'documentStepEmbed',
        attrs,
      });
    }
    if (inserted) closeStepEmbedModal();
    return inserted;
  }, [closeStepEmbedModal, stepEmbedHtml, stepEmbedMode, stepEmbedTarget, stepEmbedUrl]);

  const projectCurrentEditorPages = useCallback(() => {
    if (!editor) return null;
    const documentState = useDocumentStore.getState().currentDocument;
    const sourcePages = Array.isArray(documentState?.pages) && documentState.pages.length
      ? documentState.pages
      : pages;
    const projectionRegions = getCommonRegions(sourcePages);
    const projectionHeaderRepeats = doesHeaderRepeat(projectionRegions.headerHtml);
    const projectionHeaderSpacePx = measureRegionSpacePx(projectionRegions.headerHtml);
    const projectionRegionSpaces = {
      topSpacePx: projectionHeaderSpacePx,
      subsequentTopSpacePx: projectionHeaderRepeats ? projectionHeaderSpacePx : REGION_MIN_SPACE_PX,
      bottomSpacePx: measureRegionSpacePx(projectionRegions.footerHtml),
    };
    const liveBodyPages = getLiveBodyPagesFromEditor(editor);
    const bodyPages = liveBodyPages && liveBodyPages.length > 1
      ? liveBodyPages
      : paginateBody(materializeMathForProjection(editor.getHTML()), projectionRegionSpaces);
    const next = bodyPages.map((body, index) => {
      const topSpacePx = index === 0
        ? projectionRegionSpaces.topSpacePx
        : projectionRegionSpaces.subsequentTopSpacePx;
      return attachPageRegionMetrics(
        `${index === 0 || projectionHeaderRepeats ? projectionRegions.headerHtml : ''}${body}${renderRegionPageNumber(projectionRegions.footerHtml, index, bodyPages.length)}`,
        {
          topSpacePx,
          bottomSpacePx: projectionRegionSpaces.bottomSpacePx,
        }
      );
    });
    updateLocalPages(next);
    return next;
  }, [editor, pages, updateLocalPages]);

  const getCurrentProjectedPages = useCallback(() => {
    const documentState = useDocumentStore.getState().currentDocument;
    const sourcePages = Array.isArray(documentState?.pages) && documentState.pages.length
      ? documentState.pages
      : [''];
    if (!editor) return sourcePages;
    return projectCurrentEditorPages() || sourcePages;
  }, [editor, projectCurrentEditorPages]);

  const updatePageRegion = useCallback((regionClass, regionHtml) => {
    const documentState = useDocumentStore.getState().currentDocument;
    if (!documentState) return;
    const sourcePages = getCurrentProjectedPages();
    const sourceContainer = document.createElement('div');
    sourceContainer.innerHTML = regionHtml || '';
    const sourceRegion = sourceContainer.querySelector(`.${regionClass}`);
    if (!sourceRegion) return;
    const sourceRegionHtml = sourceRegion.outerHTML;
    const headerRepeats = regionClass === 'doc-header' &&
      sourceRegion.getAttribute('data-header-repeat') === 'all';

    const nextPages = sourcePages.map((pageHtml, index) => {
      const container = document.createElement('div');
      container.innerHTML = pageHtml || '';
      container.querySelector(`.${regionClass}`)?.remove();
      if (regionClass === 'doc-header') {
        if (index === 0 || headerRepeats) container.insertAdjacentHTML('afterbegin', sourceRegionHtml);
      } else {
        container.insertAdjacentHTML('beforeend', sourceRegionHtml);
      }
      return container.innerHTML;
    });
    updateLocalPages(nextPages);
    onDirty?.();
  }, [getCurrentProjectedPages, onDirty, updateLocalPages]);

  const handleRegionInput = useCallback((regionClass, event) => {
    const region = event.currentTarget.querySelector(`.${regionClass}`);
    const regionHtml = region?.outerHTML ?? event.currentTarget.innerHTML;
    pendingRegionEditsRef.current[regionClass] = regionHtml;
    // Footers are shared by every page. Propagate the live edit immediately so
    // newly paginated and already visible pages use the same footer content.
    if (regionClass === 'doc-footer' && region) {
      updatePageRegion(regionClass, regionHtml);
      delete pendingRegionEditsRef.current[regionClass];
    }
    onDirty?.();
  }, [onDirty, updatePageRegion]);

  const commitPendingRegionEdits = useCallback(() => {
    const pendingEntries = Object.entries(pendingRegionEditsRef.current);
    if (!pendingEntries.length) return;
    pendingRegionEditsRef.current = {};
    pendingEntries.forEach(([regionClass, regionHtml]) => {
      updatePageRegion(regionClass, regionHtml);
    });
  }, [updatePageRegion]);

  const handleRegionBlur = useCallback((regionClass, event) => {
    const region = event.currentTarget.querySelector(`.${regionClass}`);
    if (region) pendingRegionEditsRef.current[regionClass] = region.outerHTML;
    commitPendingRegionEdits();
  }, [commitPendingRegionEdits]);

  const handleRegionFocus = useCallback((regionClass, event) => {
    const region = event.currentTarget.querySelector(`.${regionClass}`);
    const placeholder = region?.querySelector('.doc-region-placeholder');
    if (!region || !placeholder || region.textContent.replace(/\u00a0/g, '').trim()) return;
    region.innerHTML = '';
    const range = document.createRange();
    range.selectNodeContents(region);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, []);

  const handleHeaderPaste = useCallback((event) => {
    const clipboard = event.clipboardData;
    const items = clipboard?.items ? Array.from(clipboard.items) : [];
    const imageItem = items.find((item) => item.type?.startsWith('image/'));
    const imageFile = imageItem?.getAsFile?.() ||
      (clipboard?.files ? Array.from(clipboard.files).find((file) => file.type?.startsWith('image/')) : null);
    const html = clipboard?.getData('text/html') || '';
    const htmlHost = document.createElement('div');
    htmlHost.innerHTML = html;
    const pastedImage = htmlHost.querySelector('img[src]');
    if (!imageFile && !pastedImage) return;

    event.preventDefault();
    const headerHost = event.currentTarget;
    const selection = window.getSelection();
    const liveRange = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const savedRange = liveRange && headerHost.contains(liveRange.commonAncestorContainer)
      ? liveRange.cloneRange()
      : null;
    if (!savedRange) return;

    const insertHeaderImage = async () => {
      const imageData = imageFile
        ? await readClipboardImage(imageFile, user?.uid)
        : {
            src: await persistImageSource(
              pastedImage.getAttribute('src'),
              user?.uid,
              pastedImage.getAttribute('alt') || 'header-image.png'
            ),
          };
      if (!headerHost.isConnected || !imageData.src) return;

      const image = document.createElement('img');
      image.src = imageData.src;
      image.alt = pastedImage?.getAttribute('alt') || imageFile?.name || 'Header image';
      image.setAttribute('data-header-image', 'true');
      image.setAttribute('data-image-align', 'left');
      image.contentEditable = 'false';

      const spacer = document.createTextNode('\u00a0');
      savedRange.deleteContents();
      savedRange.insertNode(spacer);
      savedRange.insertNode(image);
      const afterRange = document.createRange();
      afterRange.setStartAfter(spacer);
      afterRange.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(afterRange);

      const header = headerHost.querySelector('.doc-header');
      delete pendingRegionEditsRef.current['doc-header'];
      updatePageRegion('doc-header', header?.outerHTML || headerHost.innerHTML);
      setSelectedHeaderImage(image);
    };
    insertHeaderImage().catch((error) => console.error('V2 header image paste failed:', error));
  }, [updatePageRegion, user?.uid]);

  const updateHeaderImageRect = useCallback((image) => {
    if (!image?.isConnected) {
      setHeaderImageRect(null);
      return;
    }
    const rect = image.getBoundingClientRect();
    setHeaderImageRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
  }, []);

  const handleHeaderImageClick = useCallback((event) => {
    const image = event.target.closest?.('img[data-header-image="true"]');
    if (!image || !event.currentTarget.contains(image)) {
      setSelectedHeaderImage(null);
      return;
    }
    event.preventDefault();
    setSelectedHeaderImage(image);
  }, []);

  useEffect(() => {
    if (!selectedHeaderImage) {
      setHeaderImageRect(null);
      return undefined;
    }
    updateHeaderImageRect(selectedHeaderImage);
    const handleViewportChange = () => updateHeaderImageRect(selectedHeaderImage);
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);
    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [selectedHeaderImage, updateHeaderImageRect]);

  const commitHeaderImageChange = useCallback((image) => {
    const headerHost = image?.closest?.('.v2-editable-page-header');
    const header = headerHost?.querySelector('.doc-header');
    if (!header) return;
    delete pendingRegionEditsRef.current['doc-header'];
    updatePageRegion('doc-header', header.outerHTML);
  }, [updatePageRegion]);

  const handleHeaderImageResizeStart = useCallback((event, corner) => {
    event.preventDefault();
    event.stopPropagation();
    const image = selectedHeaderImage;
    if (!image) return;
    const startX = event.clientX;
    const startWidth = image.offsetWidth;
    const handleMove = (moveEvent) => {
      const delta = corner.includes('right')
        ? moveEvent.clientX - startX
        : startX - moveEvent.clientX;
      image.style.width = `${Math.max(40, startWidth + delta)}px`;
      image.style.maxHeight = 'none';
      updateHeaderImageRect(image);
    };
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      commitHeaderImageChange(image);
      setSelectedHeaderImage(null);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  }, [commitHeaderImageChange, selectedHeaderImage, updateHeaderImageRect]);

  const handleHeaderImageAlign = useCallback((alignment) => {
    if (!selectedHeaderImage) return;
    selectedHeaderImage.setAttribute('data-image-align', alignment);
    commitHeaderImageChange(selectedHeaderImage);
    setSelectedHeaderImage(null);
  }, [commitHeaderImageChange, selectedHeaderImage]);

  const handleHeaderImageDelete = useCallback(() => {
    if (!selectedHeaderImage) return;
    const image = selectedHeaderImage;
    const header = image.closest?.('.v2-editable-page-header')?.querySelector('.doc-header');
    const next = image.nextSibling;
    const previous = image.previousSibling;
    image.remove();
    if (next?.nodeType === Node.TEXT_NODE && !next.textContent.replace(/\u00a0/g, '').trim()) next.remove();
    if (previous?.nodeType === Node.TEXT_NODE && !previous.textContent.replace(/\u00a0/g, '').trim()) previous.remove();
    if (header) {
      delete pendingRegionEditsRef.current['doc-header'];
      updatePageRegion('doc-header', header.outerHTML);
    }
    setSelectedHeaderImage(null);
  }, [selectedHeaderImage, updatePageRegion]);

  const handleDeletePage = useCallback((pageIndex) => {
    if (!editor || surfacePageCount <= 1) return false;

    const bodyPages = getLiveBodyPagesFromEditor(editor) ||
      paginateBody(materializeMathForProjection(editor.getHTML()), regionSpaces);
    if (bodyPages.length <= 1 || pageIndex < 0 || pageIndex >= bodyPages.length) return false;

    bodyPages.splice(pageIndex, 1);
    const modelHtml = normalizeMathForModel(bodyPages.join('') || '<p><br></p>');
    editor.commands.setContent(modelHtml);
    migrateInlineMathStrings(editor);
    setSurfacePageCount(bodyPages.length);
    projectCurrentEditorPages();
    onDirty?.();
    return true;
  }, [editor, onDirty, projectCurrentEditorPages, regionSpaces, surfacePageCount]);

  useEffect(() => {
    deletePageRef.current = handleDeletePage;
  }, [handleDeletePage]);

  const handlePagesPointerMove = useCallback((event) => {
    const frames = Array.from(event.currentTarget.querySelectorAll('.v2-editable-page-frame'));
    const nextIndex = frames.findIndex((frame) => {
      const rect = frame.getBoundingClientRect();
      return event.clientY >= rect.top && event.clientY <= rect.bottom;
    });
    setHoveredPageIndex(nextIndex >= 0 ? nextIndex : null);
  }, []);

  useEffect(() => {
    loadedDocumentRef.current = null;
    if (embedded) return;
    if (documentId && documentId !== 'new') {
      if (!user?.uid) return;
      storeLoadDocument(documentId, { currentUserId: user.uid, isAdmin: isUserAdmin })
        .then((doc) => setDocumentName(doc?.name || 'Untitled Document'))
        .catch(() => setDocumentName('Untitled Document'));
      return;
    }
    const initial = debugInitialDocument || {
      id: null,
      name: 'Untitled Document',
      pages: [''],
      metadata: { createdAt: new Date(), updatedAt: new Date() },
    };
    setCurrentDocument(initial);
    setDocumentName(initial.name || 'Untitled Document');
  }, [debugInitialDocument, documentId, embedded, setCurrentDocument, storeLoadDocument, user?.uid, isUserAdmin]);

  useEffect(() => {
    if (!editor || !currentDocument) return;
    if (documentId && documentId !== 'new' && currentDocument.id !== documentId) return;
    if (embedded && (!documentId || documentId === 'new') && currentDocument.id && loadedDocumentRef.current === null) return;
    const identity = embedded
      ? `embedded:${documentId || (debugInitialDocument ? 'debug-new' : 'new')}`
      : currentDocument.id || (debugInitialDocument ? 'debug-new' : 'new');
    if (loadedDocumentRef.current === identity) return;
    const rawHtml = getCombinedBodyHtml(pages);
    const html = normalizeMathForModel(normalizeLoadedBodyHtml(rawHtml));
    isApplyingStateRef.current = true;
    editor.commands.setContent(html, { emitUpdate: false });
    migrateInlineMathStrings(editor);
    isApplyingStateRef.current = false;
    loadedDocumentRef.current = identity;
  }, [currentDocument, debugInitialDocument, documentId, editor, embedded, getCombinedBodyHtml, pages]);

  const persistDocumentImages = useCallback(async () => {
    if (!user?.uid) return false;
    const editorChanged = await persistEditorDataImages(editor, user.uid);
    if (editorChanged) projectCurrentEditorPages();
    let changed = editorChanged;
    const documentState = useDocumentStore.getState().currentDocument;
    const sourcePages = Array.isArray(documentState?.pages) ? documentState.pages : [];
    if (!sourcePages.some((page) => String(page || '').includes('data:image/'))) return changed;

    const persistedPages = await Promise.all(
      sourcePages.map((page) => persistHtmlDataImages(page, user.uid))
    );
    if (persistedPages.some((page, index) => page !== sourcePages[index])) {
      useDocumentStore.getState().setCurrentDocument({
        ...documentState,
        pages: persistedPages,
      });
      changed = true;
    }
    return changed;
  }, [editor, projectCurrentEditorPages, user?.uid]);

  const handleSave = useCallback(async () => {
    if (!user) return null;
    try {
      setIsSaving(true);
      commitPendingRegionEdits();
      await persistDocumentImages();
      const doc = useDocumentStore.getState().currentDocument;
      const projectedPages = editor
        ? projectCurrentEditorPages()
        : pages;
      const data = {
        name: documentName,
        pages: projectedPages || pages,
        userId: user.uid,
        assessment: doc?.assessment || null,
        paginationMode: 'preserve',
      };
      if (currentDocument?.id) {
        await updateDocument(currentDocument.id, data);
        return { id: currentDocument.id, pages: data.pages };
      } else {
        const created = await createDocument(data);
        if (created?.id) window.history.replaceState(null, '', `/document/${created.id}`);
        return created?.id ? { id: created.id, pages: data.pages } : null;
      }
    } finally {
      setIsSaving(false);
    }
  }, [commitPendingRegionEdits, createDocument, currentDocument?.id, documentName, editor, pages, persistDocumentImages, projectCurrentEditorPages, updateDocument, user]);

  const handleExport = useCallback(async () => {
    if (!user || isExporting) return;
    try {
      setIsExporting(true);
      const savedDocument = await handleSave();
      if (!savedDocument?.id) throw new Error('Document must be saved before exporting.');
      const exportFn = httpsCallable(functions, 'exportDocumentAsPdf');
      const result = await exportFn({
        documentId: savedDocument.id,
        pages: savedDocument.pages,
        layout: 'portrait',
        paginationMode: 'preserve',
      });
      if (!result.data?.pdf) throw new Error('PDF service returned no file.');
      const blob = new Blob(
        [Uint8Array.from(atob(result.data.pdf), (character) => character.charCodeAt(0))],
        { type: 'application/pdf' }
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.data.fileName || 'document.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('V2 PDF export failed:', error);
      window.alert(error.message || 'PDF export failed.');
    } finally {
      setIsExporting(false);
    }
  }, [handleSave, isExporting, user]);

  const closeMathEditor = useCallback(() => setMathEditor(null), []);

  const commitMathEditor = useCallback((latex) => {
    const instance = editorInstanceRef.current;
    const target = mathEditor;
    setMathEditor(null);
    if (!instance || !target || !latex) return;
    const chain = instance.chain().focus();
    if (target.type === 'block') {
      if (target.isNew) chain.insertBlockMath({ latex, pos: target.pos }).run();
      else chain.updateBlockMath({ latex, pos: target.pos }).run();
    } else if (target.isNew) {
      if (target.to > target.pos) {
        chain.insertContentAt(
          { from: target.pos, to: target.to },
          { type: 'inlineMath', attrs: { latex } }
        ).run();
      } else {
        chain.insertInlineMath({ latex, pos: target.pos }).run();
      }
    } else {
      chain.updateInlineMath({ latex, pos: target.pos }).run();
    }
  }, [mathEditor]);

  const executeExternalFormat = useCallback((command, value) => {
    if (!editor) return false;
    const chain = editor.chain().focus();
    switch (command) {
      case 'undo': return chain.undo().run();
      case 'redo': return chain.redo().run();
      case 'bold': {
        const listItemActive = editor.isActive('listItem');
        const nextBold = !editor.isActive('bold');
        const handled = chain.toggleBold().run();
        if (handled && listItemActive) {
          editor.chain().focus().updateAttributes('listItem', {
            markerFontWeight: nextBold ? '700' : '400',
          }).run();
        }
        return handled;
      }
      case 'italic': return chain.toggleItalic().run();
      case 'underline': return chain.toggleUnderline().run();
      case 'strikeThrough': return chain.toggleStrike().run();
      case 'justifyLeft': return chain.setTextAlign('left').run();
      case 'justifyCenter': return chain.setTextAlign('center').run();
      case 'justifyRight': return chain.setTextAlign('right').run();
      case 'justifyFull': return chain.setTextAlign('justify').run();
      case 'insertUnorderedList': return chain.toggleBulletList().run();
      case 'insertOrderedList': {
        let manualStart = null;
        let prefixFrom = null;
        let prefixLength = 0;
        const markerFontSize = editor.getAttributes('textStyle').fontSize || null;
        const markerFontWeight = editor.isActive('bold') ? '700' : null;
        if (!editor.isActive('listItem')) {
          const { $from } = editor.state.selection;
          const match = ($from.parent.textContent || '').match(/^\s*(\d+)[.)]\s+/);
          if (match) {
            manualStart = Math.max(1, Number.parseInt(match[1], 10) || 1);
            prefixFrom = $from.start();
            prefixLength = match[0].length;
          }
        }
        if (prefixFrom !== null) {
          chain.command(({ tr }) => {
            tr.delete(prefixFrom, prefixFrom + prefixLength);
            return true;
          });
        }
        const handled = chain.toggleOrderedList().run();
        if (handled && manualStart !== null) {
          editor.chain().focus().updateAttributes('orderedList', { start: manualStart }).run();
        }
        if (handled && (markerFontSize || markerFontWeight)) {
          editor.chain().focus().updateAttributes('listItem', {
            markerFontSize,
            markerFontWeight,
          }).run();
        }
        return handled;
      }
      case 'indent': return chain.indentContent().run();
      case 'outdent': return chain.outdentContent().run();
      case 'foreColor': return chain.setColor(value).run();
      case 'fontSizePx': {
        const fontSize = `${Number(value) || 14}px`;
        const listItemActive = editor.isActive('listItem');
        const handled = chain.setFontSize(fontSize).run();
        if (handled && listItemActive) {
          editor.chain().focus().updateAttributes('listItem', { markerFontSize: fontSize }).run();
        }
        return handled;
      }
      default: return false;
    }
  }, [editor]);

  const insertDocumentHeader = useCallback((firstPageOnly = true) => {
    const documentState = useDocumentStore.getState().currentDocument;
    if (!documentState) return false;
    const sourcePages = getCurrentProjectedPages();
    const nextPages = sourcePages.map((pageHtml, index) => {
      const container = document.createElement('div');
      container.innerHTML = pageHtml || '';
      let header = container.querySelector('.doc-header');
      if (firstPageOnly && index > 0) {
        header?.remove();
        return container.innerHTML;
      }
      if (!header) {
        header = document.createElement('div');
        header.className = 'doc-header';
        header.setAttribute('data-doc-header', 'true');
        header.innerHTML = '<span class="doc-region-placeholder">&nbsp;</span>';
        container.prepend(header);
      }
      if (firstPageOnly) header.removeAttribute('data-header-repeat');
      else header.setAttribute('data-header-repeat', 'all');
      return container.innerHTML;
    });
    updateLocalPages(nextPages);
    onDirty?.();
    return true;
  }, [getCurrentProjectedPages, onDirty, updateLocalPages]);

  const insertDocumentFooter = useCallback(() => {
    const documentState = useDocumentStore.getState().currentDocument;
    if (!documentState) return false;
    const sourcePages = getCurrentProjectedPages();
    const nextPages = sourcePages.map((pageHtml) => {
      const container = document.createElement('div');
      container.innerHTML = pageHtml || '';
      if (!container.querySelector('.doc-footer')) {
        const footer = document.createElement('div');
        footer.className = 'doc-footer';
        footer.setAttribute('data-doc-footer', 'true');
        footer.innerHTML = '<span class="doc-region-placeholder">&nbsp;</span>';
        container.appendChild(footer);
      }
      return container.innerHTML;
    });
    updateLocalPages(nextPages);
    onDirty?.();
    return true;
  }, [getCurrentProjectedPages, onDirty, updateLocalPages]);

  const insertDocumentPageNumber = useCallback((location = 'footer:center', startNumber = 1) => {
    if (!editor) return false;
    const [requestedRegion, requestedAlign] = String(location).split(':');
    const align = ['left', 'center', 'right'].includes(requestedAlign)
      ? requestedAlign
      : 'center';
    if (requestedRegion && requestedRegion !== 'footer') return false;

    const documentState = useDocumentStore.getState().currentDocument;
    if (!documentState) return false;
    const sourcePages = getCurrentProjectedPages();
    const totalPages = Math.max(1, surfacePageCount);
    const nextPages = Array.from({ length: totalPages }, (_, index) =>
      upsertFooterPageNumber(sourcePages[index] || '', align, index, totalPages, startNumber)
    );
    updateLocalPages(nextPages);
    onDirty?.();
    return true;
  }, [editor, getCurrentProjectedPages, onDirty, surfacePageCount, updateLocalPages]);

  const insertSectionDivider = useCallback(() => {
    if (!editor) return false;
    const dividerId = `section-divider-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const inserted = editor.chain().focus().insertContent([
      {
        type: 'sectionDivider',
        attrs: { dividerId, label: 'Section label' },
      },
      { type: 'paragraph' },
    ]).run();

    if (inserted) {
      requestAnimationFrame(() => {
        const label = editor.view.dom.querySelector(
          `[data-section-divider-id="${dividerId}"] .section-divider-label`
        );
        if (!(label instanceof HTMLElement)) return;
        label.focus();
        const range = document.createRange();
        range.selectNodeContents(label);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      });
    }
    return inserted;
  }, [editor]);

  const captureDomSelection = useCallback(() => {
    if (!editor || editor.isDestroyed) return false;
    const domSelection = window.getSelection();
    const { view } = editor;
    const { anchorNode, focusNode } = domSelection || {};
    if (
      !anchorNode ||
      !focusNode ||
      !view.dom.contains(anchorNode) ||
      !view.dom.contains(focusNode)
    ) {
      return false;
    }

    try {
      const anchor = view.posAtDOM(anchorNode, domSelection.anchorOffset);
      const head = view.posAtDOM(focusNode, domSelection.focusOffset);
      const nextSelection = TextSelection.between(
        view.state.doc.resolve(anchor),
        view.state.doc.resolve(head)
      );
      if (!nextSelection.eq(view.state.selection)) {
        view.dispatch(view.state.tr.setSelection(nextSelection));
      }
      return true;
    } catch (_) {
      return false;
    }
  }, [editor]);

  const executeTableAction = useCallback((action, options = {}) => {
    if (!editor) return false;
    captureDomSelection();
    let handled = false;
    switch (action) {
      case 'insertTable':
        handled = editor.commands.insertTable({
          rows: Math.min(8, Math.max(1, Number(options.rows) || 3)),
          cols: Math.min(8, Math.max(1, Number(options.cols) || 3)),
          withHeaderRow: options.withHeaderRow !== false,
        });
        break;
      case 'addRowBefore':
        handled = editor.commands.addRowBefore();
        break;
      case 'addRowAfter':
        handled = editor.commands.addRowAfter();
        break;
      case 'deleteRow':
        handled = editor.commands.deleteRow();
        break;
      case 'addColumnBefore':
        handled = editor.commands.addColumnBefore();
        break;
      case 'addColumnAfter':
        handled = editor.commands.addColumnAfter();
        break;
      case 'deleteColumn':
        handled = editor.commands.deleteColumn();
        break;
      case 'deleteTable':
        handled = editor.commands.deleteTable();
        break;
      default: return false;
    }
    if (handled) {
      requestAnimationFrame(() => {
        if (!editor.isDestroyed) editor.commands.focus();
      });
    }
    return handled;
  }, [captureDomSelection, editor]);

  const slashCommandMenuPortal = slashMenu.open && typeof document !== 'undefined'
    ? createPortal(
        <div
          data-v2-slash-menu="true"
          role="menu"
          aria-label="Insert block"
          className="fixed z-[100] w-64 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
          style={{ left: slashMenu.x, top: slashMenu.y }}
        >
          <div className="border-b border-gray-100 px-3 py-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Insert Block</div>
          </div>
          <div className="max-h-[360px] overflow-y-auto p-1.5">
            {SLASH_COMMANDS.map((command, index) => {
              const Icon = command.icon;
              const isSelected = slashMenu.selectedIndex === index;
              return (
                <button
                  key={command.id}
                  type="button"
                  role="menuitem"
                  className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors ${
                    isSelected
                      ? 'bg-violet-50 text-violet-900'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onMouseEnter={() => {
                    setSlashMenu((current) => {
                      const next = { ...current, selectedIndex: index };
                      slashMenuRef.current = next;
                      return next;
                    });
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => executeSlashCommand(command.id)}
                >
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
                    isSelected ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{command.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )
    : null;

  const headerImageControlsPortal = selectedHeaderImage && headerImageRect && typeof document !== 'undefined'
    ? createPortal(
        <div
          data-header-image-controls="true"
          className="fixed z-[100] pointer-events-none"
          style={{
            top: headerImageRect.top,
            left: headerImageRect.left,
            width: headerImageRect.width,
            height: headerImageRect.height,
          }}
        >
          <div className="absolute inset-0 rounded border-2 border-sigma-500" />
          {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((corner) => {
            const isTop = corner.includes('top');
            const isLeft = corner.includes('left');
            return (
              <div
                key={corner}
                className="absolute h-3 w-3 rounded-sm border-2 border-sigma-600 bg-white pointer-events-auto"
                style={{
                  top: isTop ? -6 : undefined,
                  bottom: isTop ? undefined : -6,
                  left: isLeft ? -6 : undefined,
                  right: isLeft ? undefined : -6,
                  cursor: corner === 'top-left' || corner === 'bottom-right' ? 'nwse-resize' : 'nesw-resize',
                }}
                onMouseDown={(event) => handleHeaderImageResizeStart(event, corner)}
              />
            );
          })}
          <div
            className="absolute left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 shadow-lg pointer-events-auto"
            style={{ top: headerImageRect.height + 8 }}
          >
            {[
              ['left', AlignLeft, 'Align left'],
              ['center', AlignCenter, 'Align center'],
              ['right', AlignRight, 'Align right'],
            ].map(([alignment, Icon, label]) => (
              <button
                key={alignment}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleHeaderImageAlign(alignment)}
                title={label}
                aria-label={label}
                className={`flex h-7 w-7 items-center justify-center rounded transition-colors ${
                  selectedHeaderImage.getAttribute('data-image-align') === alignment
                    ? 'bg-sigma-700 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
              </button>
            ))}
            <div className="mx-0.5 h-5 w-px bg-gray-200" />
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleHeaderImageDelete}
              title="Delete image"
              aria-label="Delete header image"
              className="flex h-7 w-7 items-center justify-center rounded text-gray-600 transition-colors hover:bg-red-50 hover:text-red-500"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  const responseAreaConfigModal = (
    <ResponseAreaConfigModal
      isOpen={!!selectedResponseAreaId}
      blockId={selectedResponseAreaId}
      config={selectedResponseAreaId
        ? currentDocument?.assessment?.responseAreas?.[selectedResponseAreaId]
        : null}
      onDuplicate={duplicateSelectedResponseArea}
      onSaved={() => onDirty?.()}
      onClose={() => setSelectedResponseAreaId(null)}
    />
  );

  const stepBlockModals = (
    <>
      <ImageUploadModal
        isOpen={!!stepImageTarget}
        userId={user?.uid}
        anchorRect={null}
        onInsert={handleStepImageInsert}
        onClose={() => setStepImageTarget(null)}
      />
      <DocumentNarrationModal
        isOpen={!!stepAudioTarget}
        isSaving={isUploadingStepAudio}
        title="Add Step Audio"
        description="Record or upload audio for this step block."
        saveLabel="Save Step Audio"
        onClose={() => {
          if (!isUploadingStepAudio) setStepAudioTarget(null);
        }}
        onSave={handleStepAudioSave}
      />
      {stepAudioError && (
        <div className="fixed bottom-5 left-1/2 z-[70] -translate-x-1/2 rounded-lg bg-red-600 px-4 py-2 text-sm text-white shadow-xl">
          {stepAudioError}
        </div>
      )}
      {stepEmbedTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Embed Content</h3>
                <p className="text-sm text-gray-500">Add HTML, an embeddable URL, or a YouTube video into this step.</p>
              </div>
              <button
                type="button"
                onClick={closeStepEmbedModal}
                className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
                title="Close"
              >
                ×
              </button>
            </div>
            <div className="space-y-4 p-5">
              <div className="flex gap-2">
                {[
                  ['html', 'HTML'],
                  ['url', 'URL'],
                  ['youtube', 'YouTube'],
                ].map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      setStepEmbedMode(mode);
                      setStepEmbedError('');
                    }}
                    className={`rounded-lg px-3 py-2 text-sm font-medium ${
                      stepEmbedMode === mode
                        ? 'bg-sigma-700 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {stepEmbedMode === 'html' ? (
                <textarea
                  value={stepEmbedHtml}
                  onChange={(event) => setStepEmbedHtml(event.target.value)}
                  rows={10}
                  placeholder="Paste embeddable HTML"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-sigma-500 focus:ring-sigma-500"
                />
              ) : (
                <input
                  value={stepEmbedUrl}
                  onChange={(event) => setStepEmbedUrl(event.target.value)}
                  placeholder={stepEmbedMode === 'youtube' ? 'YouTube URL or video ID' : 'https://example.com/embed'}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sigma-500 focus:ring-sigma-500"
                />
              )}
              {stepEmbedError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {stepEmbedError}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={closeStepEmbedModal}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStepEmbedSave}
                className="rounded-lg bg-sigma-700 px-4 py-2 text-sm font-medium text-white hover:bg-sigma-800"
              >
                Insert Embed
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  useImperativeHandle(engineRef, () => ({
    editor,
    get dom() {
      return editor?.view?.dom || null;
    },
    focus: () => editor?.commands.focus(),
    captureDomSelection,
    format: executeExternalFormat,
    openMath: (type = 'inline') => {
      if (!editor) return;
      openMathEditorRef.current({ type, pos: editor.state.selection.from, isNew: true });
    },
    insertImage: async (src, align = 'left', alt = '') => {
      if (!editor || !src) return false;
      const persistedSrc = await persistImageSource(src, user?.uid, alt || 'document-image.png');
      const dimensions = await getImageDimensions(persistedSrc);
      return editor.chain().focus().insertContent({
        type: 'resizableImage',
        attrs: { src: persistedSrc, alt, align, width: 300, ...dimensions },
      }).run();
    },
    insertMarkdown: (text) => {
      if (!editor || !text) return false;
      const inserted = editor.chain().focus().insertContent(normalizePlainTextToHtml(text)).run();
      window.setTimeout(() => migrateInlineMathStrings(editor), 0);
      return inserted;
    },
    insertSectionDivider,
    tableAction: executeTableAction,
    insertHeader: insertDocumentHeader,
    insertFooter: insertDocumentFooter,
    insertPageNumber: insertDocumentPageNumber,
    commit: async () => {
      if (!editor) return;
      commitPendingRegionEdits();
      await persistDocumentImages();
      return projectCurrentEditorPages();
    },
  }), [captureDomSelection, commitPendingRegionEdits, editor, executeExternalFormat, executeTableAction, insertDocumentFooter, insertDocumentHeader, insertDocumentPageNumber, insertSectionDivider, persistDocumentImages, projectCurrentEditorPages, user?.uid]);

  if (embedded) {
    return (
      <>
        <div
          ref={editablePagesRef}
          className="v2-editable-pages"
          style={{
            '--v2-page-count': Math.max(1, surfacePageCount),
            '--v2-page-gap': `${PAGE_GAP_PX}px`,
            '--v2-region-top-space': `${regionSpaces.topSpacePx}px`,
            '--v2-region-bottom-space': `${regionSpaces.bottomSpacePx}px`,
          }}
          onPointerMove={handlePagesPointerMove}
          onPointerLeave={() => setHoveredPageIndex(null)}
        >
          <div className="v2-editable-page-frames" contentEditable={false}>
            {Array.from({ length: surfacePageCount }, (_, index) => {
              return (
                <div
                  key={`v2-embedded-frame-${index}`}
                  className="v2-editable-page-frame"
                  style={{
                    top: `calc(${index} * (297mm + ${PAGE_GAP_PX}px))`,
                    '--v2-region-top-space': `${index === 0 ? regionSpaces.topSpacePx : regionSpaces.subsequentTopSpacePx}px`,
                    '--v2-region-bottom-space': `${regionSpaces.bottomSpacePx}px`,
                  }}
                >
                  <div data-v2-page-body-guide="true" className="v2-editable-page-body-guide" />
                </div>
              );
            })}
          </div>
          <div className="v2-editable-page-regions" contentEditable={false}>
            {Array.from({ length: surfacePageCount }, (_, index) => {
              const pageHtml = pages[index] || '';
              const { headerHtml, footerHtml } = getPageRegions(pageHtml || '');
              const visibleHeaderHtml = index === 0 || headerRepeatsOnAllPages
                ? (headerHtml || regions.headerHtml)
                : '';
              const visibleFooterHtml = renderRegionPageNumber(
                footerHtml || regions.footerHtml,
                index,
                surfacePageCount
              );
              return (
                <div
                  key={`v2-embedded-regions-${index}`}
                  className="v2-editable-page-region-layer"
                  style={{
                    top: `calc(${index} * (297mm + ${PAGE_GAP_PX}px))`,
                  }}
                >
                  <EditablePageRegion
                    className="v2-editable-page-header"
                    html={visibleHeaderHtml}
                    regionClass="doc-header"
                    editable={!!visibleHeaderHtml}
                    onFocus={handleRegionFocus}
                    onInput={handleRegionInput}
                    onBlur={handleRegionBlur}
                    onPaste={handleHeaderPaste}
                    onClick={handleHeaderImageClick}
                  />
                  <EditablePageRegion
                    className="v2-editable-page-footer"
                    html={visibleFooterHtml}
                    regionClass="doc-footer"
                    editable={!!visibleFooterHtml}
                    onInput={handleRegionInput}
                    onBlur={handleRegionBlur}
                  />
                </div>
              );
            })}
          </div>
          <EditorContent editor={editor} />
          {surfacePageCount > 1 && (
            <div className="v2-page-delete-controls" contentEditable={false}>
              {Array.from({ length: surfacePageCount }, (_, index) => (
                <button
                  key={`v2-embedded-delete-page-${index}`}
                  type="button"
                  className={`v2-page-delete-button${hoveredPageIndex === index ? ' is-visible' : ''}`}
                  style={{ top: `calc(${index} * (297mm + ${PAGE_GAP_PX}px) + 8px)` }}
                  onClick={() => handleDeletePage(index)}
                  title={`Delete page ${index + 1}`}
                  aria-label={`Delete page ${index + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ))}
            </div>
          )}
        </div>
        {createPortal(
          <InlineMathModal
            isOpen={!!mathEditor}
            initialLatex={mathEditor?.latex || ''}
            anchorRect={mathEditor?.anchorRect || null}
            onInsert={commitMathEditor}
            onClose={closeMathEditor}
          />,
          document.body
        )}
        {slashCommandMenuPortal}
        {headerImageControlsPortal}
        {responseAreaConfigModal}
        {stepBlockModals}
      </>
    );
  }

  return (
    <div className={className || 'h-full flex flex-col bg-gray-100'}>
      <div className="bg-sigma-800 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <span className="text-sm font-semibold">Document Editor V2 (Model-Owned Pagination)</span>
          <input
            value={documentName}
            onChange={(event) => setDocumentName(event.target.value)}
            className="min-w-0 max-w-xs rounded border border-white/20 bg-white/10 px-2 py-1 text-sm text-white placeholder:text-white/60 focus:border-white/50 focus:outline-none"
            placeholder="Document name"
            aria-label="Document name"
          />
          <button
            type="button"
            className="text-xs px-2 py-1 rounded bg-white/15 hover:bg-white/25"
            onClick={() => {
              const u = new URL(window.location.href);
              u.searchParams.set('editor', 'legacy');
              window.history.replaceState(null, '', u.toString());
              window.location.reload();
            }}
          >
            Revert to Legacy
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 rounded border border-white/30 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/10"
            disabled={isSaving || isExporting}
          >
            <Download className="w-4 h-4" />
            {isExporting ? 'Exporting...' : 'Export PDF'}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-white text-sigma-800 text-sm font-medium"
            disabled={isSaving || isExporting}
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-8">
        <div className="mx-auto" style={{ width: `${PAGE_WIDTH_MM}mm` }}>
          <div className="v2-model-editor-shell">
            <div className="v2-model-toolbar" contentEditable={false}>
              <button type="button" onClick={() => editor?.chain().focus().toggleBold().run()}>Bold</button>
              <button type="button" onClick={() => editor?.chain().focus().toggleItalic().run()}>Italic</button>
              <button type="button" onClick={() => editor?.chain().focus().toggleBulletList().run()}>Bullets</button>
              <button type="button" onClick={() => executeExternalFormat('insertOrderedList')}>Numbering</button>
              <button
                type="button"
                onClick={() => {
                  if (!editor) return;
                  openMathEditorRef.current({
                    type: 'inline',
                    pos: editor.state.selection.from,
                    isNew: true,
                  });
                }}
              >
                Inline math
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!editor) return;
                  openMathEditorRef.current({
                    type: 'block',
                    pos: editor.state.selection.from,
                    isNew: true,
                  });
                }}
              >
                Display math
              </button>
              <button type="button" onClick={() => editor?.chain().focus().undo().run()}>Undo</button>
              <button type="button" onClick={() => editor?.chain().focus().redo().run()}>Redo</button>
              <span className="v2-model-page-count">{surfacePageCount} editable pages</span>
            </div>
            <div
              ref={editablePagesRef}
              className="v2-editable-pages"
              style={{
                '--v2-page-count': Math.max(1, surfacePageCount),
                '--v2-page-gap': `${PAGE_GAP_PX}px`,
                '--v2-region-top-space': `${regionSpaces.topSpacePx}px`,
                '--v2-region-bottom-space': `${regionSpaces.bottomSpacePx}px`,
              }}
              onPointerMove={handlePagesPointerMove}
              onPointerLeave={() => setHoveredPageIndex(null)}
            >
              <div className="v2-editable-page-frames" contentEditable={false}>
                {Array.from({ length: surfacePageCount }, (_, index) => {
                  return (
                    <div
                      key={`v2-editable-frame-${index}`}
                      className="v2-editable-page-frame"
                      style={{
                        top: `calc(${index} * (297mm + ${PAGE_GAP_PX}px))`,
                        '--v2-region-top-space': `${index === 0 ? regionSpaces.topSpacePx : regionSpaces.subsequentTopSpacePx}px`,
                        '--v2-region-bottom-space': `${regionSpaces.bottomSpacePx}px`,
                      }}
                    >
                      <div data-v2-page-body-guide="true" className="v2-editable-page-body-guide" />
                    </div>
                  );
                })}
              </div>
              <div className="v2-editable-page-regions" contentEditable={false}>
                {Array.from({ length: surfacePageCount }, (_, index) => {
                  const pageHtml = pages[index] || '';
                  const { headerHtml, footerHtml } = getPageRegions(pageHtml || '');
                  const visibleHeaderHtml = index === 0 || headerRepeatsOnAllPages
                    ? (headerHtml || regions.headerHtml)
                    : '';
                  const visibleFooterHtml = renderRegionPageNumber(
                    footerHtml || regions.footerHtml,
                    index,
                    surfacePageCount
                  );
                  return (
                    <div
                      key={`v2-editable-regions-${index}`}
                      className="v2-editable-page-region-layer"
                      style={{
                        top: `calc(${index} * (297mm + ${PAGE_GAP_PX}px))`,
                      }}
                    >
                      <EditablePageRegion
                        className="v2-editable-page-header"
                        html={visibleHeaderHtml}
                        regionClass="doc-header"
                        editable={!!visibleHeaderHtml}
                        onFocus={handleRegionFocus}
                        onInput={handleRegionInput}
                        onBlur={handleRegionBlur}
                        onPaste={handleHeaderPaste}
                        onClick={handleHeaderImageClick}
                      />
                      <EditablePageRegion
                        className="v2-editable-page-footer"
                        html={visibleFooterHtml}
                        regionClass="doc-footer"
                        editable={!!visibleFooterHtml}
                        onInput={handleRegionInput}
                        onBlur={handleRegionBlur}
                      />
                    </div>
                  );
                })}
              </div>
              <EditorContent editor={editor} />
              {surfacePageCount > 1 && (
                <div className="v2-page-delete-controls" contentEditable={false}>
                  {Array.from({ length: surfacePageCount }, (_, index) => (
                    <button
                      key={`v2-delete-page-${index}`}
                      type="button"
                      className={`v2-page-delete-button${hoveredPageIndex === index ? ' is-visible' : ''}`}
                      style={{ top: `calc(${index} * (297mm + ${PAGE_GAP_PX}px) + 8px)` }}
                      onClick={() => handleDeletePage(index)}
                      title={`Delete page ${index + 1}`}
                      aria-label={`Delete page ${index + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {createPortal(
        <InlineMathModal
          isOpen={!!mathEditor}
          initialLatex={mathEditor?.latex || ''}
          anchorRect={mathEditor?.anchorRect || null}
          onInsert={commitMathEditor}
          onClose={closeMathEditor}
        />,
        document.body
      )}
      {slashCommandMenuPortal}
      {headerImageControlsPortal}
      {responseAreaConfigModal}
      {stepBlockModals}
    </div>
  );
};

export default DocumentEditorV2;
